import React, { useCallback, useEffect, useMemo, useState } from "react";
import Plot from "react-plotly.js";

import Layout from "@components/Layout";
import { Card, CardContent } from "@components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import { ECON_API_BASE_URL } from "@lib/config";
import { fmtLocalDate } from "@lib/dateUtils";
import { downloadEconSnapshot } from "@lib/econSnapshot";
import { useUser } from "../hooks/useUser";
import { useTheme } from "../theme/ThemeProvider";
import { getEconChartPalette } from "../theme/chartTheme";
import MLResultsTab from "../components/ui/MLResultsTab";
import QuickNav from "@components/cockpit/QuickNav";

type ReactProcessEnv = {
  readonly REACT_APP_FUNC_KEY?: string;
};

declare const process: {
  readonly env: ReactProcessEnv;
};

type ScoreBucket = "expansionary" | "cautionary" | "recessionary";

type EconIndicator = {
  series_id: string;
  label: string;
  value: number;
  score: number;
  score_label: string;
  class: string;
  type: string;
  theme?: string;
  cadence?: string;
  unit?: string;
  model_usage?: string;
  include_in_model?: boolean;
  include_in_breadth?: boolean;
  context_role?: string | null;
  source_series_id?: string | null;
  derived_from?: string[] | null;
  source?: string;
  interpretation?: string;
  date: string;
};

type RegimeHistory = {
  dates: string[];
  expansionary: number[];
  cautionary: number[];
  recessionary: number[];
  weakening_prob: number[];
};

type EconHistory = {
  series_id: string;
  label: string;
  dates: string[];
  values: Array<number | null>;
  cadence?: string;
  window_label?: string;
  source?: string;
  total_points?: number;
  filtered_points?: number;
  returned_points?: number;
  latest_date?: string | null;
  latest_value?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  class?: string | null;
  type?: string | null;
  theme?: string | null;
  unit?: string | null;
  interpretation?: string | null;
  include_in_model?: boolean;
  include_in_breadth?: boolean;
  context_role?: string | null;
  source_series_id?: string | null;
  derived_from?: string[] | null;
  generated_at?: string;
};

type EconDimension = {
  theme: string;
  label: string;
  average_score: number | null;
  status: string;
  counts: Record<ScoreBucket, number>;
  indicator_count: number;
  all_indicator_count?: number;
  context_count?: number;
  watch_items?: EconIndicator[];
  support_items?: EconIndicator[];
  context_items?: EconIndicator[];
};

type HistoryTimeframe = "YTD" | "1Y" | "3Y" | "5Y" | "10Y" | "MAX";

type HistoryCacheEntry = {
  fetchedAt: string;
  history: EconHistory;
};

type EconOverview = {
  generated_at: string;
  as_of: string | null;
  freshness: {
    status: string;
    latest_date: string | null;
    earliest_date: string | null;
    stale_count: number;
    aging_count: number;
    total_series: number;
    cadence_counts?: Record<string, number>;
  };
  regime: {
    current: {
      label: string;
      date: string | null;
      expansionary: number;
      cautionary: number;
      recessionary: number;
      weakening_prob: number;
      delta_vs_prior: {
        expansionary: number;
        cautionary: number;
        recessionary: number;
      };
    };
    history: RegimeHistory;
  };
  summary: {
    expansionary: number;
    cautionary: number;
    recessionary: number;
    by_type: Record<string, Record<ScoreBucket, number>>;
    by_class: Record<string, Record<ScoreBucket, number>>;
    by_dimension?: Record<string, Record<ScoreBucket, number>>;
    context_only?: number;
  };
  dimensions?: Record<string, EconDimension>;
  drivers: {
    supports: EconIndicator[];
    risks: EconIndicator[];
  };
  narrative: {
    headline: string;
    body: string;
    portfolio: string;
  };
  histories: Record<string, EconHistory>;
  indicators: EconIndicator[];
  // Series that FAILED to load (blob read threw) — returned by econ/overview but previously
  // never read, so a dropped feed silently vanished from the board (2026-07-01 audit P1 #23).
  missing_indicators?: { series_id?: string; label?: string; type?: string | null; theme?: string | null; reason?: string | null }[];
  early_warning?: {
    signal?: string | null;
    thresholds?: {
      sentinel?: number;
      watch?: number;
      caution?: number;
      severe?: number;
      recovery?: number;
    } | null;
    operating_bands?: Record<string, { lower?: number; upper?: number; use?: string }> | null;
    radar_state?: string | null;
    threshold?: number | null;
    rolling_weakening_prob?: number | null;
    rolling_severity_score?: number | null;
    weakening_prob?: number | null;
    severity_score?: number | null;
    delta_3m?: number | null;
    latest_trigger?: { kind?: string; date: string; weakening_prob: number; delta_3m: number } | null;
    latest_recovery?: { kind?: string; date: string; weakening_prob: number; delta_3m: number } | null;
  } | null;
  ml_status?: {
    model_run_id?: string | null;
    model_trained_at_utc?: string | null;
    scored_at_utc?: string | null;
  } | null;
};

type CadenceBucket = "daily" | "weekly" | "monthly" | "quarterly" | "unknown";

type LocalFreshnessStatus = "fresh" | "aging" | "stale";

type LocalFreshnessSummary = {
  status: LocalFreshnessStatus;
  agingCount: number;
  staleCount: number;
  totalSeries: number;
  latestDate: string | null;
  earliestDate: string | null;
  cadenceCounts: Record<CadenceBucket, number>;
};

const HISTORY_TIMEFRAMES: Array<{ key: HistoryTimeframe; label: string }> = [
  { key: "YTD", label: "YTD" },
  { key: "1Y", label: "1Y" },
  { key: "3Y", label: "3Y" },
  { key: "5Y", label: "5Y" },
  { key: "10Y", label: "10Y" },
  { key: "MAX", label: "MAX" },
];

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function timeframeStartDate(timeframe: HistoryTimeframe, latestDate?: string | null): string | null {
  if (timeframe === "MAX") return null;

  const anchor = parseIsoDate(latestDate) ?? new Date();
  const base = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), anchor.getUTCDate()));

  switch (timeframe) {
    case "YTD":
      return `${base.getUTCFullYear()}-01-01`;
    case "1Y":
      base.setUTCFullYear(base.getUTCFullYear() - 1);
      return isoDate(base);
    case "3Y":
      base.setUTCFullYear(base.getUTCFullYear() - 3);
      return isoDate(base);
    case "5Y":
      base.setUTCFullYear(base.getUTCFullYear() - 5);
      return isoDate(base);
    case "10Y":
      base.setUTCFullYear(base.getUTCFullYear() - 10);
      return isoDate(base);
    default:
      return null;
  }
}

function timeframeWindowLabel(timeframe: HistoryTimeframe): string {
  switch (timeframe) {
    case "YTD":
      return "Year to date";
    case "1Y":
      return "Last 1 year";
    case "3Y":
      return "Last 3 years";
    case "5Y":
      return "Last 5 years";
    case "10Y":
      return "Last 10 years";
    case "MAX":
      return "Full blob history";
    default:
      return "Selected window";
  }
}

function historyCacheKey(seriesId: string, timeframe: HistoryTimeframe): string {
  return `${seriesId}__${timeframe}`;
}

const FUNCTION_KEY = (process.env.REACT_APP_FUNC_KEY || "").trim();

function appendFunctionKey(url: string): string {
  if (!FUNCTION_KEY) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}code=${encodeURIComponent(FUNCTION_KEY)}`;
}

function windowHistoryForTimeframe(history: EconHistory | null | undefined, timeframe: HistoryTimeframe, latestDate?: string | null): EconHistory | null {
  if (!history) return null;
  if (timeframe === "MAX") {
    return { ...history, window_label: history.window_label || timeframeWindowLabel(timeframe) };
  }

  const startDate = timeframeStartDate(timeframe, latestDate || history.latest_date || history.end_date || null);
  if (!startDate) {
    return { ...history, window_label: timeframeWindowLabel(timeframe) };
  }

  const startTime = new Date(`${startDate}T00:00:00Z`).getTime();
  const pairs = history.dates
    .map((date, index) => ({ date, value: history.values[index] ?? null, time: new Date(`${date}T00:00:00Z`).getTime() }))
    .filter((point) => Number.isFinite(point.time) && point.time >= startTime);

  const dates = pairs.map((point) => point.date);
  const values = pairs.map((point) => point.value);
  const latestIndex = values.length - 1;

  return {
    ...history,
    dates,
    values,
    returned_points: dates.length,
    filtered_points: dates.length,
    window_label: timeframeWindowLabel(timeframe),
    start_date: dates[0] ?? null,
    end_date: dates[latestIndex] ?? null,
    latest_date: dates[latestIndex] ?? history.latest_date ?? null,
    latest_value: values[latestIndex] ?? history.latest_value ?? null,
  };
}

function refreshSummaryText(payload: any): string {
  const savedCount = Number(payload?.saved_count ?? 0);
  const skippedCount = Number(payload?.skipped_count ?? 0);
  const failedCount = Number(payload?.failed_count ?? 0);
  const savedSeries = Array.isArray(payload?.saved)
    ? payload.saved.slice(0, 6).map((item: any) => item?.series_id).filter(Boolean).join(", ")
    : "";
  const suffix = savedSeries ? ` Saved: ${savedSeries}${savedCount > 6 ? ", …" : ""}.` : "";
  return `FRED refresh complete: ${savedCount} saved, ${skippedCount} skipped, ${failedCount} failed.${suffix}`;
}

const ui = {
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  sectionBg: "rgba(148, 163, 184, 0.10)",
  shadow: "var(--shadow-soft)",
};

// Chart palette (type + getEconChartPalette) now lives in ../theme/chartTheme —
// shared macro-regime base, de-duplicated with the ML dashboard.

const cardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};

const softCardStyle: React.CSSProperties = {
  backgroundColor: ui.sectionBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
};

const bucketTone: Record<ScoreBucket, { text: string; chipBg: string; border: string }> = {
  expansionary: {
    text: "var(--status-positive-text)",
    chipBg: "var(--status-positive-bg)",
    border: "var(--status-positive-border)",
  },
  cautionary: {
    text: "var(--status-warning-text)",
    chipBg: "var(--status-warning-bg)",
    border: "var(--status-warning-border)",
  },
  recessionary: {
    text: "var(--status-danger-text)",
    chipBg: "var(--status-danger-bg)",
    border: "var(--status-danger-border)",
  },
};

type MethodologySectionKey = "overview" | "indicators" | "playbook" | "operations";

type MethodologyIndicator = {
  bucket: string;
  symbol: string;
  name: string;
  use: string;
  how: string;
  modelUse: string;
};

const METHODOLOGY_SECTIONS: Array<{ key: MethodologySectionKey; label: string }> = [
  { key: "overview", label: "Framework" },
  { key: "indicators", label: "Indicator Guide" },
  { key: "playbook", label: "How to Use" },
  { key: "operations", label: "Data & Refresh" },
];


const SERIES_LABEL_OVERRIDES: Record<string, string> = {
  RSXFS: "Retail Sales: Retail Trade YoY",
  DRCCLACBS: "Credit Card Loan Delinquency Rate",
  BAMLH0A0HYM2: "High Yield Credit Spread — Recent Window",
  CSUSHPINSA_REAL_YOY: "Case-Shiller Real Home Price YoY",
  CSUSHPINSA_3M_ANN: "Case-Shiller Housing Wealth Impulse",
};

const TEXT_LABEL_REPLACEMENTS: Array<[RegExp, string]> = [
  [/Retail Sales ex-Auto YoY/g, "Retail Sales: Retail Trade YoY"],
  [/Consumer Loan Delinquency Rate/g, "Credit Card Loan Delinquency Rate"],
  [/High Yield Credit Spread(?!\s—\sRecent Window)/g, "High Yield Credit Spread — Recent Window"],
];

function displaySeriesLabel(seriesId?: string | null, fallback?: string | null): string {
  if (seriesId && SERIES_LABEL_OVERRIDES[seriesId]) return SERIES_LABEL_OVERRIDES[seriesId];
  return cleanDashboardText(fallback || "");
}

function cleanDashboardText(text?: string | null): string {
  if (!text) return "";
  return TEXT_LABEL_REPLACEMENTS.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), text);
}

function normalizeIndicatorLabels(item: EconIndicator): EconIndicator {
  return {
    ...item,
    label: displaySeriesLabel(item.series_id, item.label),
  };
}

function normalizeHistoryLabels(item: EconHistory): EconHistory {
  return {
    ...item,
    label: displaySeriesLabel(item.series_id, item.label),
    interpretation: item.interpretation ? cleanDashboardText(item.interpretation) : item.interpretation,
  };
}

function normalizeDimensionLabels(dimension: EconDimension): EconDimension {
  return {
    ...dimension,
    watch_items: dimension.watch_items?.map(normalizeIndicatorLabels),
    support_items: dimension.support_items?.map(normalizeIndicatorLabels),
    context_items: dimension.context_items?.map(normalizeIndicatorLabels),
  };
}

function normalizeOverviewLabels(payload: EconOverview): EconOverview {
  const histories = Object.fromEntries(
    Object.entries(payload.histories || {}).map(([key, history]) => [key, normalizeHistoryLabels(history)])
  ) as Record<string, EconHistory>;

  const dimensions = payload.dimensions
    ? Object.fromEntries(
        Object.entries(payload.dimensions).map(([key, dimension]) => [key, normalizeDimensionLabels(dimension)])
      ) as Record<string, EconDimension>
    : payload.dimensions;

  return {
    ...payload,
    dimensions,
    indicators: (payload.indicators || []).map(normalizeIndicatorLabels),
    drivers: {
      supports: (payload.drivers?.supports || []).map(normalizeIndicatorLabels),
      risks: (payload.drivers?.risks || []).map(normalizeIndicatorLabels),
    },
    histories,
    narrative: {
      headline: cleanDashboardText(payload.narrative?.headline || ""),
      body: cleanDashboardText(payload.narrative?.body || ""),
      portfolio: cleanDashboardText(payload.narrative?.portfolio || ""),
    },
  };
}

const METHODOLOGY_BUCKETS = [
  {
    name: "Growth Pulse",
    question: "Is real activity expanding, slowing, or contracting?",
    guidance: "Use CFNAI/CFNAIMA3 and industrial production as the broad growth read. Heavy truck sales, profits, and GDP help confirm the story, but GDP is lagged and should not dominate the dashboard.",
  },
  {
    name: "Labor Stress",
    question: "Is the labor market still absorbing shocks, or is unemployment deterioration accelerating?",
    guidance: "Claims and job openings often move first. Payrolls and unemployment confirm. Sahm Rule is the high-conviction labor stress trigger when unemployment deterioration becomes persistent.",
  },
  {
    name: "Inflation / Fed Pressure",
    question: "Does inflation give the Fed room to help, or does it keep policy tight?",
    guidance: "Core CPI/Core PCE and wage pressure determine whether weakness is benign disinflation or a harder stagflation-style setup.",
  },
  {
    name: "Financial Conditions / Liquidity",
    question: "Are credit, rates, and liquidity getting tighter before the hard economy breaks?",
    guidance: "Curve inversion, HY spread widening, and NFCI tightening are first-alert conditions. They usually matter before GDP confirms anything.",
  },
  {
    name: "Consumer / Housing",
    question: "Are households and housing still supporting demand?",
    guidance: "Housing permits and retail sales are useful cycle reads. Sentiment is softer data. Credit-card delinquencies confirm household stress later in the cycle. Case-Shiller real home-price momentum and the 3-month housing wealth impulse are shown as structural housing-wealth context only, not as AML features or headline breadth drivers.",
  },
  {
    name: "GDP / Nowcast Context",
    question: "Does the official growth scoreboard confirm or contradict faster indicators?",
    guidance: "Real GDP and GDPNow are narrative/context tools. They are useful for CIO communication, but the model should still lean on faster growth, labor, credit, and inflation signals.",
  },
];

const METHODOLOGY_INDICATORS: MethodologyIndicator[] = [
  {
    bucket: "Growth Pulse",
    symbol: "CFNAI",
    name: "Chicago Fed National Activity Index",
    use: "Broad monthly activity composite.",
    how: "Below-trend readings warn that overall activity is softening. One month matters less than a persistent deterioration.",
    modelUse: "Primary growth feature",
  },
  {
    bucket: "Growth Pulse",
    symbol: "CFNAIMA3",
    name: "CFNAI 3-Month Average",
    use: "Smoothed broad activity pulse.",
    how: "This is one of the best summary growth signals. Persistent weakness should increase caution even before GDP confirms it.",
    modelUse: "Primary growth feature",
  },
  {
    bucket: "Growth Pulse",
    symbol: "INDPRO",
    name: "Industrial Production",
    use: "Hard production-side activity.",
    how: "Weak or negative readings confirm cyclical slowing in the real economy, especially manufacturing and production-sensitive sectors.",
    modelUse: "Primary growth feature",
  },
  {
    bucket: "Growth Pulse",
    symbol: "HTRUCKSSA",
    name: "Heavy Truck Sales",
    use: "Transport and capital-spending proxy.",
    how: "Sharp declines can warn that freight, logistics, and business investment are cooling.",
    modelUse: "Supporting growth feature",
  },
  {
    bucket: "Growth Pulse",
    symbol: "CPROFIT",
    name: "Corporate Profits",
    use: "Profit-cycle confirmation.",
    how: "Useful for earnings pressure confirmation, but quarterly and lagged, so it should not lead the regime call.",
    modelUse: "Context / confirmation",
  },
  {
    bucket: "Labor Stress",
    symbol: "ICSA",
    name: "Initial Jobless Claims",
    use: "Weekly layoff pressure.",
    how: "Rising claims are an early labor warning. Claims rising with Sahm movement is much more serious than claims alone.",
    modelUse: "Primary labor feature",
  },
  {
    bucket: "Labor Stress",
    symbol: "PAYEMS",
    name: "Nonfarm Payrolls",
    use: "Payroll employment growth.",
    how: "Slowing payroll growth confirms labor demand is cooling. It is important, but not always early.",
    modelUse: "Primary labor feature",
  },
  {
    bucket: "Labor Stress",
    symbol: "UNRATE",
    name: "Unemployment Rate",
    use: "Headline unemployment.",
    how: "Rising unemployment is confirmation. Interpret alongside participation and Sahm Rule rather than in isolation.",
    modelUse: "Primary labor feature",
  },
  {
    bucket: "Labor Stress",
    symbol: "SAHMREALTIME",
    name: "Sahm Rule Realtime Indicator",
    use: "Labor deterioration trigger.",
    how: "Near or above 0.50 percentage points is historically a serious recession warning. Use it as high-conviction confirmation, not as the first signal.",
    modelUse: "High-conviction labor trigger",
  },
  {
    bucket: "Labor Stress",
    symbol: "JTSJOL",
    name: "JOLTS Job Openings",
    use: "Labor-demand pipeline.",
    how: "Falling openings suggest firms are reducing labor demand before layoffs fully appear.",
    modelUse: "Supporting labor feature",
  },
  {
    bucket: "Labor Stress",
    symbol: "JTSQUL",
    name: "JOLTS Quits",
    use: "Worker confidence and labor tightness.",
    how: "Falling quits suggest workers are less confident and job switching is cooling.",
    modelUse: "Supporting labor feature",
  },
  {
    bucket: "Labor Stress",
    symbol: "JTSHIR",
    name: "JOLTS Hires Rate",
    use: "Hiring pace.",
    how: "Lower hiring means firms are getting cautious even if layoffs have not spiked yet.",
    modelUse: "Supporting labor feature",
  },
  {
    bucket: "Labor Stress",
    symbol: "CIVPART",
    name: "Labor Force Participation Rate",
    use: "Labor-supply context.",
    how: "Use this to interpret unemployment. A rising unemployment rate means something different if participation is also rising.",
    modelUse: "Context",
  },
  {
    bucket: "Inflation / Fed Pressure",
    symbol: "CPIAUCSL",
    name: "Headline CPI",
    use: "Broad consumer inflation.",
    how: "High inflation keeps Fed pressure high. Very low inflation can indicate demand weakness.",
    modelUse: "Primary inflation feature",
  },
  {
    bucket: "Inflation / Fed Pressure",
    symbol: "CPILFESL",
    name: "Core CPI",
    use: "CPI excluding food and energy.",
    how: "Better for sticky inflation than headline CPI. Important for whether weak growth becomes easier policy or stagflation risk.",
    modelUse: "Primary inflation feature",
  },
  {
    bucket: "Inflation / Fed Pressure",
    symbol: "PCEPI",
    name: "PCE Price Index",
    use: "Fed-relevant inflation lens.",
    how: "Use it for the Fed policy narrative. It complements CPI and can differ due to weighting and substitution effects.",
    modelUse: "Primary inflation feature",
  },
  {
    bucket: "Inflation / Fed Pressure",
    symbol: "PCEPILFE",
    name: "Core PCE Price Index",
    use: "Core Fed-relevant inflation.",
    how: "One of the most important Fed-pressure measures. Sticky core PCE limits the policy rescue path.",
    modelUse: "Primary inflation feature",
  },
  {
    bucket: "Inflation / Fed Pressure",
    symbol: "PPIACO",
    name: "Producer Price Index: All Commodities",
    use: "Upstream price pressure.",
    how: "Useful for goods inflation, commodity pressure, and margin squeeze context.",
    modelUse: "Supporting inflation feature",
  },
  {
    bucket: "Inflation / Fed Pressure",
    symbol: "AHETPI",
    name: "Average Hourly Earnings",
    use: "Wage pressure.",
    how: "Wage acceleration can keep services inflation sticky. Wage collapse can also indicate labor stress.",
    modelUse: "Supporting inflation/labor feature",
  },
  {
    bucket: "Financial Conditions / Liquidity",
    symbol: "T10Y2Y",
    name: "10-Year minus 2-Year Treasury Spread",
    use: "Yield-curve slope.",
    how: "Sustained inversion is a monetary-tightness warning. Re-steepening after inversion can also matter.",
    modelUse: "Primary financial conditions feature",
  },
  {
    bucket: "Financial Conditions / Liquidity",
    symbol: "T10Y3M",
    name: "10-Year minus 3-Month Treasury Spread",
    use: "Policy-sensitive yield-curve slope.",
    how: "Because the 3-month bill is tied closely to Fed policy, this curve is a serious tightening warning when inverted.",
    modelUse: "Primary financial conditions feature",
  },
  {
    bucket: "Financial Conditions / Liquidity",
    symbol: "BAA10YM",
    name: "Baa Credit Spread vs 10Y Treasury",
    use: "Long-history corporate credit-spread backbone.",
    how: "Wider Baa spreads show tighter credit and weaker risk appetite. This is now the primary historical credit-spread feature for AML training because it has a much longer usable history.",
    modelUse: "Primary historical credit-stress feature",
  },
  {
    bucket: "Financial Conditions / Liquidity",
    symbol: "BAMLH0A0HYM2",
    name: "High Yield Option-Adjusted Spread — Recent Window",
    use: "Live below-investment-grade credit stress context.",
    how: "Widening HY spreads are still valuable for current dashboard interpretation, but this series is kept out of the core AML trainer because the FRED/ICE window is currently short.",
    modelUse: "Live context / recent-window dashboard feature",
  },
  {
    bucket: "Financial Conditions / Liquidity",
    symbol: "NFCI",
    name: "Chicago Fed National Financial Conditions Index",
    use: "Broad financial tightening / loosening.",
    how: "Positive readings are tighter than average; negative readings are looser. Watch it with HY spreads and curve stress.",
    modelUse: "Primary financial conditions feature",
  },
  {
    bucket: "Financial Conditions / Liquidity",
    symbol: "M2SL",
    name: "M2 Money Supply",
    use: "Broad money/liquidity context.",
    how: "Helpful when liquidity is contracting or surging, but policy and methodology changes can distort it.",
    modelUse: "Context",
  },
  {
    bucket: "Financial Conditions / Liquidity",
    symbol: "BOGMBASE",
    name: "Monetary Base",
    use: "Base-money liquidity context.",
    how: "Useful for liquidity backdrop, but not a standalone recession signal.",
    modelUse: "Context",
  },
  {
    bucket: "Consumer / Housing",
    symbol: "PERMIT",
    name: "Building Permits",
    use: "Housing pipeline.",
    how: "Falling permits are a leading warning that the housing cycle is weakening.",
    modelUse: "Primary housing feature",
  },
  {
    bucket: "Consumer / Housing",
    symbol: "RSXFS",
    name: "Advance Retail Sales: Retail Trade",
    use: "Consumer spending proxy.",
    how: "Slowing retail sales warn that household demand is losing momentum.",
    modelUse: "Primary consumer feature",
  },
  {
    bucket: "Consumer / Housing",
    symbol: "UMCSENT",
    name: "University of Michigan Consumer Sentiment",
    use: "Consumer mood and expectations.",
    how: "Very weak sentiment can foreshadow softer spending, but sentiment alone should not drive regime calls.",
    modelUse: "Supporting consumer feature",
  },
  {
    bucket: "Consumer / Housing",
    symbol: "DRCCLACBS",
    name: "Credit Card Loan Delinquency Rate",
    use: "Household credit stress.",
    how: "Rising delinquencies confirm household balance-sheet pressure, usually later than permits or sentiment.",
    modelUse: "Consumer stress confirmation",
  },
  {
    bucket: "Consumer / Housing",
    symbol: "CSUSHPINSA_REAL_YOY",
    name: "Case-Shiller Real Home Price YoY",
    use: "Structural housing-wealth context.",
    how: "Use it to understand whether housing wealth is supporting household balance sheets after inflation. It is lagged monthly context, so it should not drive live allocation or AML scoring by itself.",
    modelUse: "Dashboard structural context / excluded from AML",
  },
  {
    bucket: "Consumer / Housing",
    symbol: "CSUSHPINSA_3M_ANN",
    name: "Case-Shiller Housing Wealth Impulse",
    use: "Shorter structural housing-wealth impulse.",
    how: "Use the 3-month annualized change as a lagged confirmation of whether housing wealth is accelerating, stalling, or rolling over. It remains dashboard context only.",
    modelUse: "Dashboard structural context / excluded from AML",
  },
  {
    bucket: "GDP / Nowcast Context",
    symbol: "GDPC1",
    name: "Real Gross Domestic Product",
    use: "Official real growth scoreboard.",
    how: "Context only. It is quarterly, delayed, and revised, so it should validate the story rather than lead it.",
    modelUse: "Dashboard context / low-weight feature",
  },
  {
    bucket: "GDP / Nowcast Context",
    symbol: "GDPNOW",
    name: "Atlanta Fed GDPNow",
    use: "Current-quarter GDP nowcast.",
    how: "Timelier than official GDP, but model-based. Use for current-quarter context, not as a hard trigger.",
    modelUse: "Dashboard context",
  },
  {
    bucket: "Training / Labels",
    symbol: "USREC",
    name: "NBER Recession Indicator",
    use: "Historical recession label.",
    how: "This is the answer key for training and backtesting. It is not a live decision signal.",
    modelUse: "Training label only",
  },
];

const METHODOLOGY_PLAYBOOK = [
  {
    title: "Recession-warning setup",
    detail: "Curve inversion or re-steepening after inversion + HY spread widening + NFCI tightening + rising claims + Sahm moving toward 0.50 + weakening CFNAIMA3/INDPRO.",
  },
  {
    title: "Stagflation / Fed-boxed-in setup",
    detail: "Growth weakens while Core CPI/Core PCE and wage pressure remain sticky. This is more dangerous because the Fed has less room to ease.",
  },
  {
    title: "Soft-landing setup",
    detail: "Growth slows modestly, inflation falls, claims remain stable, HY spreads stay contained, and financial conditions do not tighten aggressively.",
  },
  {
    title: "Risk-on expansion setup",
    detail: "CFNAI improves, payrolls remain stable, claims stay low, credit spreads are contained, inflation cools, and financial conditions remain easy.",
  },
];


type DashboardRiskStatus = "expansionary" | "sentinel" | "monitor" | "watch" | "caution" | "high_risk";

type DashboardRiskPresentation = {
  status: DashboardRiskStatus;
  label: string;
  tone: ScoreBucket;
  badgeText: string;
  summary: string;
  detail: string;
};

function toFraction(n?: number | null): number | null {
  if (n == null || Number.isNaN(n)) return null;
  const value = Number(n);
  if (!Number.isFinite(value)) return null;
  return Math.abs(value) > 1 ? value / 100 : value;
}

function maxDefined(...values: Array<number | null | undefined>): number {
  const filtered = values.filter((value): value is number => value != null && Number.isFinite(Number(value))).map((value) => Number(value));
  return filtered.length ? Math.max(...filtered) : 0;
}

function buildDashboardRiskPresentation(args: {
  rawSignal?: string | null;
  radarState?: string | null;
  weakeningProb?: number | null;
  rollingWeakeningProb?: number | null;
  thresholds?: {
    sentinel?: number;
    watch?: number;
    caution?: number;
    severe?: number;
    recovery?: number;
  } | null;
  delta3m?: number | null;
}): DashboardRiskPresentation {
  const sentinel = args.thresholds?.sentinel ?? 0.25;
  const recovery = args.thresholds?.recovery ?? 0.35;
  const watch = args.thresholds?.watch ?? 0.45;
  const caution = args.thresholds?.caution ?? 0.55;
  const severe = args.thresholds?.severe ?? 0.68;
  const weakening = toFraction(args.weakeningProb);
  const rolling = toFraction(args.rollingWeakeningProb);
  const signal = (args.rawSignal || "").toLowerCase();
  const radarState = (args.radarState || "").toLowerCase();
  const delta = args.delta3m ?? 0;
  const effective = rolling ?? weakening ?? 0;

  if (signal === "severe_deterioration" || radarState === "severe_deterioration" || effective >= severe) {
    return {
      status: "high_risk",
      label: "High Risk",
      tone: "recessionary",
      badgeText: "High Risk",
      summary: "Deterioration risk is elevated and persistent.",
      detail: delta > 0.03
        ? "Probability is well above the high-risk band and still rising over the last three months."
        : "Probability is well above the high-risk band, though the recent rate of deterioration is less acute.",
    };
  }

  if (signal === "caution" || radarState === "caution" || effective >= caution) {
    return {
      status: "caution",
      label: "Caution",
      tone: "cautionary",
      badgeText: "Caution",
      summary: "Conditions warrant closer monitoring and a more selective stance.",
      detail: delta > 0.03
        ? "Weakening risk is above the caution band and has been trending higher recently."
        : "Weakening risk is above the caution band, but momentum is not accelerating sharply.",
    };
  }

  if (signal === "watch" || radarState === "watch" || effective >= watch) {
    return {
      status: "watch",
      label: "Watch",
      tone: "cautionary",
      badgeText: "Watch",
      summary: "Early cracks are showing, but the signal is not yet in a high-conviction warning state.",
      detail: "The dashboard is flagging drift in the macro tape without implying a full defensive posture.",
    };
  }

  if (radarState === "monitor" || (effective >= recovery && effective < watch)) {
    return {
      status: "monitor",
      label: "Monitor",
      tone: "cautionary",
      badgeText: "Monitor",
      summary: "Risk is above the recovery boundary but below the formal Watch band.",
      detail: "This is a trend-monitoring state. It should prompt attention to drivers and market confirmation, not an automatic posture change.",
    };
  }

  if (radarState === "sentinel" || (effective >= sentinel && effective < recovery)) {
    return {
      status: "sentinel",
      label: "Sentinel",
      tone: "cautionary",
      badgeText: "Sentinel",
      summary: "The model is in early radar territory, but not in a formal warning band.",
      detail: "Sentinel is a diagnostics/monitoring state. It should improve awareness without creating an automatic posture change.",
    };
  }

  return {
    status: "expansionary",
    label: "Expansion",
    tone: "expansionary",
    badgeText: "Expansion",
    summary: "Macro conditions are broadly supportive.",
    detail: "The current dashboard read is consistent with a normal expansionary backdrop rather than a live warning regime.",
  };
}


function pct(n?: number | null, digits = 1): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(digits)}%`;
}

function smartPct(n?: number | null, digits = 1): string {
  if (n == null || Number.isNaN(n)) return "—";
  const value = Number(n);
  const normalized = Math.abs(value) > 1 ? value : value * 100;
  return `${normalized.toFixed(digits)}%`;
}

function num(n?: number | null, digits = 2): string {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toFixed(digits);
}

function formatIndicatorValue(item?: Pick<EconIndicator, "value" | "unit"> | null, overrideValue?: number | null): string {
  const rawValue = overrideValue ?? item?.value;
  if (rawValue == null || Number.isNaN(Number(rawValue))) return "—";
  const value = Number(rawValue);
  const unit = (item?.unit || "").toLowerCase();
  if (unit === "pct" || unit === "pct_points" || unit === "pct_points_annualized") {
    return `${value.toFixed(1)}%`;
  }
  if (unit === "persons") {
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  if (unit === "thousands") {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}k`;
  }
  if (unit === "billions_usd") {
    return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}B`;
  }
  if (Math.abs(value) >= 1000) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function deltaPct(n?: number | null, digits = 1): string {
  if (n == null || Number.isNaN(n)) return "—";
  const sign = Number(n) > 0 ? "+" : "";
  return `${sign}${(Number(n) * 100).toFixed(digits)} pts`;
}

type OperatingThresholds = {
  sentinel: number;
  monitor: number;
  watch: number;
  caution: number;
  severe: number;
};

function movingAverage(values: Array<number | null>, windowSize = 3): Array<number | null> {
  return values.map((_, idx) => {
    const start = Math.max(0, idx - windowSize + 1);
    const window = values.slice(start, idx + 1).filter((value): value is number => value != null && Number.isFinite(value));
    if (!window.length) return null;
    return window.reduce((acc, value) => acc + value, 0) / window.length;
  });
}

function bandForProbability(probability: number | null | undefined, thresholds: OperatingThresholds): string {
  const value = toFraction(probability) ?? 0;
  if (value >= thresholds.severe) return "Severe";
  if (value >= thresholds.caution) return "Caution";
  if (value >= thresholds.watch) return "Watch";
  if (value >= thresholds.monitor) return "Monitor";
  if (value >= thresholds.sentinel) return "Sentinel";
  return "Benign";
}

function latestNumeric(values: Array<number | null | undefined>): number | null {
  for (let idx = values.length - 1; idx >= 0; idx -= 1) {
    const value = values[idx];
    if (value != null && Number.isFinite(Number(value))) return Number(value);
  }
  return null;
}

function latestNumericDelta(values: Array<number | null | undefined>): number | null {
  const valid = values.filter((value): value is number => value != null && Number.isFinite(Number(value))).map(Number);
  if (valid.length < 2) return null;
  return valid[valid.length - 1] - valid[valid.length - 2];
}

function consecutivePeriodsAtOrAbove(values: Array<number | null | undefined>, thresholdPct: number): number {
  let count = 0;
  for (let idx = values.length - 1; idx >= 0; idx -= 1) {
    const value = values[idx];
    if (value == null || !Number.isFinite(Number(value)) || Number(value) < thresholdPct) break;
    count += 1;
  }
  return count;
}

function trendDescriptor(delta3m?: number | null): string {
  const value = toFraction(delta3m) ?? 0;
  if (value >= 0.15) return "Rising sharply";
  if (value >= 0.05) return "Rising modestly";
  if (value <= -0.15) return "Falling sharply";
  if (value <= -0.05) return "Falling modestly";
  return "Stable";
}

function reviewActionForBand(band: string): string {
  switch (band) {
    case "Severe":
      return "High-priority CIO macro review";
    case "Caution":
      return "Active posture review";
    case "Watch":
      return "Formal CIO macro review";
    case "Monitor":
      return "Driver review and monthly CIO note";
    case "Sentinel":
      return "Enhanced radar only";
    default:
      return "Normal monitoring";
  }
}

function persistencePhrase(count: number): string {
  if (count <= 0) return "not currently above";
  if (count === 1) return "1 month above";
  return `${count} months above`;
}

// Short pills for the indicator-monitor themes. The h3 in the monitor keeps the
// full theme name; only the nav label is abbreviated. Unknown themes fall back to
// the segment before the slash, so a new catalog theme still gets a sane pill.
const GROUP_NAV_LABELS: Record<string, string> = {
  "Growth Pulse": "Growth",
  "Labor Stress": "Labor",
  "Inflation / Fed Pressure": "Inflation",
  "Financial Conditions / Liquidity": "Financial",
  "Consumer / Housing": "Consumer",
  "GDP / Nowcast Context": "GDP",
};

function groupNavLabel(groupName: string): string {
  return GROUP_NAV_LABELS[groupName] ?? groupName.split("/")[0].trim();
}

function groupAnchorId(groupName: string): string {
  const slug = groupName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return `econ-grp-${slug || "other"}`;
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function parseIsoDate(dateStr?: string | null): Date | null {
  if (!dateStr) return null;
  const parsed = new Date(`${dateStr}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function diffDays(from: Date, to: Date): number {
  const ms = to.getTime() - from.getTime();
  return Math.floor(ms / 86400000);
}

function inferCadenceFromHistory(history?: EconHistory | null): CadenceBucket {
  if (!history) return "unknown";

  const cadence = (history.cadence || "").toLowerCase();
  if (cadence.includes("day")) return "daily";
  if (cadence.includes("week")) return "weekly";
  if (cadence.includes("month")) return "monthly";
  if (cadence.includes("quarter")) return "quarterly";

  const parsedDates = history.dates
    .map((d) => parseIsoDate(d))
    .filter((d): d is Date => Boolean(d));

  if (parsedDates.length < 2) return "unknown";

  const deltas: number[] = [];
  for (let i = 1; i < parsedDates.length; i += 1) {
    const delta = diffDays(parsedDates[i - 1], parsedDates[i]);
    if (delta > 0) deltas.push(delta);
  }

  if (!deltas.length) return "unknown";

  const sorted = [...deltas].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];

  if (median <= 3) return "daily";
  if (median <= 10) return "weekly";
  if (median <= 45) return "monthly";
  if (median <= 120) return "quarterly";
  return "unknown";
}

function freshnessThresholds(cadence: CadenceBucket): { aging: number; stale: number } {
  switch (cadence) {
    case "daily":
      return { aging: 4, stale: 10 };
    case "weekly":
      return { aging: 10, stale: 21 };
    case "monthly":
      return { aging: 45, stale: 90 };
    case "quarterly":
      return { aging: 105, stale: 150 };
    default:
      return { aging: 21, stale: 60 };
  }
}

function computeCadenceAwareFreshness(
  indicators: EconIndicator[],
  histories: Record<string, EconHistory>,
  fallback: EconOverview["freshness"]
): LocalFreshnessSummary {
  const today = new Date();
  const cadenceCounts: Record<CadenceBucket, number> = {
    daily: 0,
    weekly: 0,
    monthly: 0,
    quarterly: 0,
    unknown: 0,
  };

  const validDates: string[] = [];
  let agingCount = 0;
  let staleCount = 0;

  for (const item of indicators) {
    const dt = parseIsoDate(item.date);
    if (!dt) continue;

    validDates.push(item.date);

    const cadenceFromItem = (item.cadence || "").toLowerCase();
    const cadence: CadenceBucket = cadenceFromItem.includes("day")
      ? "daily"
      : cadenceFromItem.includes("week")
        ? "weekly"
        : cadenceFromItem.includes("month")
          ? "monthly"
          : cadenceFromItem.includes("quarter")
            ? "quarterly"
            : inferCadenceFromHistory(histories[item.series_id]);
    cadenceCounts[cadence] += 1;

    const ageDays = diffDays(dt, today);
    const thresholds = freshnessThresholds(cadence);

    if (ageDays > thresholds.stale) {
      staleCount += 1;
    } else if (ageDays > thresholds.aging) {
      agingCount += 1;
    }
  }

  const totalSeries = indicators.length || fallback.total_series || 0;

  let status: LocalFreshnessStatus = "fresh";
  if (totalSeries > 0) {
    const staleRatio = staleCount / totalSeries;
    const agingRatio = agingCount / totalSeries;

    if (staleCount >= 5 || staleRatio >= 0.35) {
      status = "stale";
    } else if (agingCount >= 4 || agingRatio >= 0.25) {
      status = "aging";
    }
  } else {
    status = fallback.status === "stale" ? "stale" : fallback.status === "aging" ? "aging" : "fresh";
  }

  const sortedDates = validDates.slice().sort();

  return {
    status,
    agingCount,
    staleCount,
    totalSeries,
    latestDate: sortedDates.length ? sortedDates[sortedDates.length - 1] : fallback.latest_date,
    earliestDate: sortedDates.length ? sortedDates[0] : fallback.earliest_date,
    cadenceCounts,
  };
}

type PlotThresholdLine = {
  label: string;
  value: number;
  color?: string;
  labelColor?: string;
  dash?: "solid" | "dot" | "dash";
};

type PlotSeries = {
  name: string;
  values: Array<number | null>;
  color: string;
  width?: number;
  dash?: "solid" | "dot" | "dash";
  opacity?: number;
};

const PlotCard: React.FC<{
  title: string;
  subtitle?: string;
  dates: string[];
  series: PlotSeries[];
  valueSuffix?: string;
  thresholdLines?: PlotThresholdLine[];
}> = ({ title, subtitle, dates, series, valueSuffix = "", thresholdLines = [] }) => {
  const { theme } = useTheme();
  const chart = useMemo(() => getEconChartPalette(theme), [theme]);
  const hasData = dates.length > 0 && series.some((item) => item.values.some((v) => v != null));
  const validThresholdLines = thresholdLines.filter((line) => Number.isFinite(line.value));

  return (
    <Card style={cardStyle}>
      <CardContent className="p-4">
        <div className="mb-3">
          <h3 className="text-base font-semibold">{title}</h3>
          {subtitle ? <p className="text-xs" style={{ color: ui.textMuted }}>{subtitle}</p> : null}
        </div>
        {hasData ? (
          <Plot
            data={series.map((item) => ({
              x: dates,
              y: item.values,
              type: "scatter",
              mode: "lines",
              name: item.name,
              line: { width: item.width ?? 2, color: item.color, dash: item.dash ?? "solid" },
              opacity: item.opacity ?? 1,
              hovertemplate: `%{x}<br>${item.name}: %{y:.2f}${valueSuffix}<extra></extra>`,
            }))}
            layout={{
              autosize: true,
              height: 260,
              margin: { l: 40, r: validThresholdLines.length ? 74 : 20, t: 10, b: 36 },
              paper_bgcolor: "transparent",
              plot_bgcolor: "transparent",
              font: { color: chart.axisText, size: 12 },
              legend: { orientation: "h", y: 1.15, x: 0, font: { color: chart.axisText } },
              shapes: validThresholdLines.map((line) => ({
                type: "line",
                xref: "paper",
                yref: "y",
                x0: 0,
                x1: 1,
                y0: line.value,
                y1: line.value,
                line: {
                  color: line.color || chart.thresholdDefault,
                  width: 1,
                  dash: line.dash || "dot",
                },
              })),
              annotations: validThresholdLines.map((line) => ({
                xref: "paper",
                yref: "y",
                x: 1.01,
                y: line.value,
                xanchor: "left",
                yanchor: "middle",
                showarrow: false,
                text: `${line.label} ${line.value.toFixed(0)}${valueSuffix}`,
                font: { size: 10, color: line.labelColor || line.color || chart.mutedText },
              })),
              xaxis: {
                showgrid: false,
                tickfont: { color: chart.mutedText },
                automargin: true,
              },
              yaxis: {
                gridcolor: chart.gridLine,
                zerolinecolor: chart.zeroLine,
                tickfont: { color: chart.mutedText },
                automargin: true,
              },
            }}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: "100%", height: "260px" }}
            useResizeHandler
          />
        ) : (
          <div className="h-[260px] flex items-center justify-center text-sm" style={{ color: ui.textMuted }}>
            No chart data available.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MetricCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  tone?: ScoreBucket;
}> = ({ title, value, subtitle, tone }) => {
  const toneStyle = tone ? bucketTone[tone] : null;

  return (
    <Card style={cardStyle}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>
          {title}
        </div>
        <div className="text-2xl font-semibold" style={{ color: toneStyle?.text ?? ui.textPrimary }}>
          {value}
        </div>
        {subtitle ? <div className="mt-2 text-sm" style={{ color: ui.textSecondary }}>{subtitle}</div> : null}
      </CardContent>
    </Card>
  );
};


// --- Macro Narrative presentation -------------------------------------------------
// The narrative used to render as one run-on paragraph. These blocks show the same
// facts as scannable rows; the raw engine text stays available under the card's
// "Full narrative text" disclosure so any drift is visible rather than hidden.

const indicatorTone = (item: Pick<EconIndicator, "score">): ScoreBucket =>
  item.score >= 2 ? "expansionary" : item.score === 1 ? "cautionary" : "recessionary";

const NarrativeSection: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <div>
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className="text-xs uppercase tracking-wide font-medium" style={{ color: ui.textMuted }}>{title}</span>
      {hint ? <span className="text-xs" style={{ color: ui.textMuted }}>{hint}</span> : null}
    </div>
    <div className="mt-2">{children}</div>
  </div>
);

const NarrativeChips: React.FC<{ items: EconIndicator[]; tone?: ScoreBucket; emptyText: string }> = ({ items, tone, emptyText }) => {
  if (!items.length) return <div className="text-sm" style={{ color: ui.textMuted }}>{emptyText}</div>;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const itemTone = bucketTone[tone ?? indicatorTone(item)];
        const value = formatIndicatorValue(item);
        return (
          <span
            key={item.series_id}
            className="text-xs px-2.5 py-1 rounded-full whitespace-nowrap"
            style={{ backgroundColor: itemTone.chipBg, color: itemTone.text, border: `1px solid ${itemTone.border}` }}
            title={`${item.type} \u2022 ${item.class} \u2022 ${item.score_label} \u2022 ${fmtLocalDate(item.date)}`}
          >
            {item.label}{value !== "\u2014" ? ` ${value}` : ""}
          </span>
        );
      })}
    </div>
  );
};

const NarrativeStatGrid: React.FC<{ items: EconIndicator[]; tone?: ScoreBucket }> = ({ items, tone }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
    {items.map((item) => (
      <div key={item.series_id} className="rounded-lg px-3 py-2" style={softCardStyle}>
        <div className="text-xs leading-4" style={{ color: ui.textMuted }}>{item.label}</div>
        <div className="text-base font-semibold mt-0.5" style={{ color: bucketTone[tone ?? indicatorTone(item)].text }}>
          {formatIndicatorValue(item)}
        </div>
      </div>
    ))}
  </div>
);


const IndicatorHistoryModal: React.FC<{
  indicator: EconIndicator | null;
  history: EconHistory | null;
  isLoading: boolean;
  loadError: string | null;
  selectedTimeframe: HistoryTimeframe;
  onTimeframeChange: (timeframe: HistoryTimeframe) => void;
  onClose: () => void;
}> = ({ indicator, history, isLoading, loadError, selectedTimeframe, onTimeframeChange, onClose }) => {
  const { theme } = useTheme();
  const chart = useMemo(() => getEconChartPalette(theme), [theme]);

  useEffect(() => {
    if (!indicator) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [indicator, onClose]);

  if (!indicator) return null;

  const tone = indicator.score >= 2 ? bucketTone.expansionary : indicator.score === 1 ? bucketTone.cautionary : bucketTone.recessionary;
  const cadenceFromIndicator = (history?.cadence || indicator.cadence || "").toLowerCase();
  const cadence: CadenceBucket = cadenceFromIndicator.includes("day")
    ? "daily"
    : cadenceFromIndicator.includes("week")
      ? "weekly"
      : cadenceFromIndicator.includes("month")
        ? "monthly"
        : cadenceFromIndicator.includes("quarter")
          ? "quarterly"
          : inferCadenceFromHistory(history);
  const hasData = Boolean(history?.dates?.length) && Boolean(history?.values?.some((value) => value != null));
  const observations = history?.total_points ?? history?.dates?.length ?? 0;
  const returnedPoints = history?.returned_points ?? history?.dates?.length ?? 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden p-3 sm:p-6"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.68)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="indicator-history-modal-title"
    >
      <div
        className="flex max-h-[calc(100vh-1.5rem)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl sm:max-h-[calc(100vh-3rem)]"
        style={{ ...cardStyle, border: `1px solid ${ui.cardBorder}` }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex flex-shrink-0 items-start justify-between gap-4 border-b p-5" style={{ borderColor: ui.cardBorder }}>
          <div>
            <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Indicator Time Series</div>
            <h2 id="indicator-history-modal-title" className="text-xl font-semibold">{indicator.label}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" style={{ color: ui.textMuted }}>
              <span>{history?.theme || indicator.theme || history?.type || indicator.type}</span>
              <span>•</span>
              <span>{history?.class || indicator.class}</span>
              <span>•</span>
              <span>{titleCase(cadence)}</span>
              <span>•</span>
              <span>Updated {fmtLocalDate(history?.latest_date || indicator.date)}</span>
              {history?.source ? (
                <>
                  <span>•</span>
                  <span>Source: {history.source}</span>
                </>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm font-medium transition"
            style={{
              backgroundColor: ui.sectionBg,
              color: ui.textPrimary,
              border: `1px solid ${ui.cardBorder}`,
            }}
            aria-label="Close indicator history"
          >
            Close
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5 pb-8 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {HISTORY_TIMEFRAMES.map((timeframe) => {
              const isActive = timeframe.key === selectedTimeframe;
              return (
                <button
                  key={timeframe.key}
                  type="button"
                  onClick={() => onTimeframeChange(timeframe.key)}
                  className="rounded-full px-3 py-1.5 text-xs font-semibold transition"
                  style={{
                    backgroundColor: isActive ? "rgba(37, 99, 235, 0.16)" : ui.sectionBg,
                    color: isActive ? "#1d4ed8" : ui.textSecondary,
                    border: `1px solid ${isActive ? "rgba(37, 99, 235, 0.32)" : ui.cardBorder}`,
                  }}
                >
                  {timeframe.label}
                </button>
              );
            })}
          </div>

          {loadError ? (
            <div
              className="rounded-lg px-3 py-2 text-sm"
              style={{
                backgroundColor: "var(--status-warning-bg)",
                border: "1px solid var(--status-warning-border)",
                color: "var(--status-warning-text)",
              }}
            >
              {loadError}
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Latest Value</div>
              <div className="text-lg font-semibold">{formatIndicatorValue(indicator, history?.latest_value ?? indicator.value)}</div>
            </div>
            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Signal</div>
              <div
                className="inline-flex rounded-full px-2.5 py-1 text-xs font-medium"
                style={{
                  backgroundColor: tone.chipBg,
                  color: tone.text,
                  border: `1px solid ${tone.border}`,
                }}
              >
                {indicator.score_label}
              </div>
            </div>
            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Observations</div>
              <div className="text-lg font-semibold">{observations}</div>
              {history && returnedPoints !== observations ? (
                <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Showing {returnedPoints} chart points</div>
              ) : null}
            </div>
            <div className="rounded-lg p-3" style={softCardStyle}>
              <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Window</div>
              <div className="text-sm font-medium" style={{ color: ui.textSecondary }}>
                {history?.window_label || timeframeWindowLabel(selectedTimeframe)}
              </div>
            </div>
          </div>

          <Card style={cardStyle}>
            <CardContent className="p-4">
              {isLoading && !hasData ? (
                <div className="h-[320px] flex items-center justify-center text-sm" style={{ color: ui.textMuted }}>
                  Loading full indicator history from Blob…
                </div>
              ) : hasData && history ? (
                <Plot
                  data={[
                    {
                      x: history.dates,
                      y: history.values,
                      type: "scatter",
                      mode: "lines",
                      name: history.label || indicator.label,
                      line: { width: 2.5, color: chart.indicatorLine },
                      hovertemplate: `%{x}<br>${history.label || indicator.label}: %{y:.2f}<extra></extra>`,
                    },
                  ]}
                  layout={{
                    autosize: true,
                    height: 440,
                    margin: { l: 56, r: 24, t: 16, b: 48 },
                    paper_bgcolor: "transparent",
                    plot_bgcolor: "transparent",
                    font: { color: chart.axisText, size: 12 },
                    showlegend: false,
                    xaxis: {
                      showgrid: false,
                      tickfont: { color: chart.mutedText },
                      automargin: true,
                    },
                    yaxis: {
                      gridcolor: chart.gridLine,
                      zerolinecolor: chart.zeroLine,
                      tickfont: { color: chart.mutedText },
                      automargin: true,
                    },
                  }}
                  config={{ displayModeBar: false, responsive: true }}
                  style={{ width: "100%", height: "440px" }}
                  useResizeHandler
                />
              ) : (
                <div className="h-[320px] flex items-center justify-center text-sm" style={{ color: ui.textMuted }}>
                  No historical chart data is available for this indicator yet.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};


const MethodologyModal: React.FC<{
  open: boolean;
  onClose: () => void;
  sentinelThreshold?: number | null;
  watchThreshold?: number | null;
  cautionThreshold?: number | null;
  severeThreshold?: number | null;
}> = ({ open, onClose, sentinelThreshold, watchThreshold, cautionThreshold, severeThreshold }) => {
  const [activeSection, setActiveSection] = useState<MethodologySectionKey>("overview");

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const sentinel = sentinelThreshold ?? 0.25;
  const monitor = 0.35;
  const watch = watchThreshold ?? 0.45;
  const caution = cautionThreshold ?? 0.55;
  const severe = severeThreshold ?? 0.68;
  const groupedMethodologyIndicators = METHODOLOGY_INDICATORS.reduce<Record<string, MethodologyIndicator[]>>((acc, item) => {
    if (!acc[item.bucket]) acc[item.bucket] = [];
    acc[item.bucket].push(item);
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center overflow-hidden p-3 sm:p-6"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.72)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="economic-methodology-modal-title"
    >
      <div
        className="flex max-h-[calc(100vh-1.5rem)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl sm:max-h-[calc(100vh-3rem)]"
        style={{ ...cardStyle, border: `1px solid ${ui.cardBorder}` }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex flex-shrink-0 items-start justify-between gap-4 border-b p-5" style={{ borderColor: ui.cardBorder }}>
          <div>
            <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Economic Dashboard Reference</div>
            <h2 id="economic-methodology-modal-title" className="text-xl font-semibold">Methodology</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6" style={{ color: ui.textSecondary }}>
              A compact reference for what each macro bucket measures, why each FRED symbol exists in the model, and how the dashboard should be used in CIO review.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm font-medium transition"
            style={{
              backgroundColor: ui.sectionBg,
              color: ui.textPrimary,
              border: `1px solid ${ui.cardBorder}`,
            }}
            aria-label="Close methodology reference"
          >
            Close
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5 pb-10 space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            {METHODOLOGY_SECTIONS.map((section) => {
              const isActive = section.key === activeSection;
              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setActiveSection(section.key)}
                  className="rounded-full px-3 py-1.5 text-xs font-semibold transition"
                  style={{
                    backgroundColor: isActive ? "rgba(37, 99, 235, 0.16)" : ui.sectionBg,
                    color: isActive ? "#60a5fa" : ui.textSecondary,
                    border: `1px solid ${isActive ? "rgba(96, 165, 250, 0.35)" : ui.cardBorder}`,
                  }}
                >
                  {section.label}
                </button>
              );
            })}
          </div>

          {activeSection === "overview" ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <Card style={cardStyle} className="xl:col-span-2">
                  <CardContent className="p-5 space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold">Purpose</h3>
                      <p className="mt-2 text-sm leading-6" style={{ color: ui.textSecondary }}>
                        The economic dashboard is a CIO decision-support layer. It is designed to explain macro regime risk, not to auto-generate trades. It separates the economy into growth, labor, inflation, financial conditions, consumer/housing, and GDP context so one stale or lagging series does not dominate the portfolio narrative.
                      </p>
                    </div>
                    <div className="rounded-lg p-4" style={softCardStyle}>
                      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Core Rule</div>
                      <div className="text-sm leading-6" style={{ color: ui.textSecondary }}>
                        Do not trade off one indicator. The dashboard is most useful when several buckets agree: financial stress first, growth/labor confirmation second, and GDP as the official scoreboard.
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Sentinel</div>
                        <div className="text-lg font-semibold">{smartPct(sentinel)}</div>
                        <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Early radar only; not a formal warning.</div>
                      </div>
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Monitor</div>
                        <div className="text-lg font-semibold">{smartPct(monitor)}</div>
                        <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Trend deserves attention, no posture change by itself.</div>
                      </div>
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Watch</div>
                        <div className="text-lg font-semibold">{smartPct(watch)}</div>
                        <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Formal CIO review watch band.</div>
                      </div>
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Caution</div>
                        <div className="text-lg font-semibold">{smartPct(caution)}</div>
                        <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Closer CIO review and more selective posture.</div>
                      </div>
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Severe</div>
                        <div className="text-lg font-semibold">{smartPct(severe)}</div>
                        <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Persistent high-conviction deterioration signal.</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card style={cardStyle}>
                  <CardContent className="p-5 space-y-3">
                    <h3 className="text-lg font-semibold">What the Scores Mean</h3>
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <span className="inline-flex rounded-full px-2 py-1 text-xs font-medium" style={{ backgroundColor: bucketTone.expansionary.chipBg, color: bucketTone.expansionary.text, border: `1px solid ${bucketTone.expansionary.border}` }}>Expansionary</span>
                      <p className="mt-2 text-sm" style={{ color: ui.textSecondary }}>Supportive or improving macro signal.</p>
                    </div>
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <span className="inline-flex rounded-full px-2 py-1 text-xs font-medium" style={{ backgroundColor: bucketTone.cautionary.chipBg, color: bucketTone.cautionary.text, border: `1px solid ${bucketTone.cautionary.border}` }}>Cautionary</span>
                      <p className="mt-2 text-sm" style={{ color: ui.textSecondary }}>Mixed, aging, or warning-level signal.</p>
                    </div>
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <span className="inline-flex rounded-full px-2 py-1 text-xs font-medium" style={{ backgroundColor: bucketTone.recessionary.chipBg, color: bucketTone.recessionary.text, border: `1px solid ${bucketTone.recessionary.border}` }}>Stress</span>
                      <p className="mt-2 text-sm" style={{ color: ui.textSecondary }}>Deterioration signal that should be checked against other buckets.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {METHODOLOGY_BUCKETS.map((bucket) => (
                  <div key={bucket.name} className="rounded-xl p-4" style={softCardStyle}>
                    <h4 className="font-semibold">{bucket.name}</h4>
                    <div className="mt-2 text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Question</div>
                    <p className="mt-1 text-sm leading-5" style={{ color: ui.textSecondary }}>{bucket.question}</p>
                    <div className="mt-3 text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Use</div>
                    <p className="mt-1 text-sm leading-5" style={{ color: ui.textSecondary }}>{bucket.guidance}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {activeSection === "indicators" ? (
            <div className="space-y-5">
              <div className="rounded-lg p-4" style={softCardStyle}>
                <h3 className="text-lg font-semibold">FRED Symbol Guide</h3>
                <p className="mt-2 text-sm leading-6" style={{ color: ui.textSecondary }}>
                  FRED symbols are economic-data tickers. Use the plain-English name and bucket first; the symbol is mainly the backend identifier that powers data pulls, scoring, and charts.
                </p>
              </div>

              {Object.entries(groupedMethodologyIndicators).map(([bucketName, indicators]) => (
                <Card key={bucketName} style={cardStyle}>
                  <CardContent className="p-5">
                    <h3 className="text-lg font-semibold mb-3">{bucketName}</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {indicators.map((item) => (
                        <div key={`${item.bucket}-${item.symbol}`} className="rounded-lg p-3" style={softCardStyle}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>{item.symbol}</div>
                              <div className="font-semibold">{item.name}</div>
                            </div>
                            <span className="rounded-full px-2 py-1 text-xs font-medium whitespace-nowrap" style={{ backgroundColor: "rgba(37, 99, 235, 0.12)", color: "#60a5fa", border: "1px solid rgba(96, 165, 250, 0.28)" }}>
                              {item.modelUse}
                            </span>
                          </div>
                          <div className="mt-3 text-sm leading-5" style={{ color: ui.textSecondary }}>
                            <div><span className="font-medium" style={{ color: ui.textPrimary }}>Why:</span> {item.use}</div>
                            <div className="mt-1"><span className="font-medium" style={{ color: ui.textPrimary }}>How:</span> {item.how}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : null}

          {activeSection === "playbook" ? (
            <div className="space-y-5">
              <Card style={cardStyle}>
                <CardContent className="p-5">
                  <h3 className="text-lg font-semibold">Interpretation Playbook</h3>
                  <p className="mt-2 text-sm leading-6" style={{ color: ui.textSecondary }}>
                    The dashboard is strongest when it reads combinations across buckets. These are the four reference patterns to use during CIO review.
                  </p>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {METHODOLOGY_PLAYBOOK.map((item) => (
                      <div key={item.title} className="rounded-lg p-4" style={softCardStyle}>
                        <h4 className="font-semibold">{item.title}</h4>
                        <p className="mt-2 text-sm leading-6" style={{ color: ui.textSecondary }}>{item.detail}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <Card style={cardStyle}>
                  <CardContent className="p-5">
                    <h3 className="text-lg font-semibold">Most Important Early Warnings</h3>
                    <p className="mt-2 text-sm leading-6" style={{ color: ui.textSecondary }}>
                      T10Y3M/T10Y2Y, HY spreads, NFCI, claims, Sahm Rule, CFNAIMA3, INDPRO, and permits. When several deteriorate together, the macro read is materially more important.
                    </p>
                  </CardContent>
                </Card>
                <Card style={cardStyle}>
                  <CardContent className="p-5">
                    <h3 className="text-lg font-semibold">Fed-Pressure Read</h3>
                    <p className="mt-2 text-sm leading-6" style={{ color: ui.textSecondary }}>
                      Core CPI, Core PCE, wages, and PPI determine whether weakness is easing-friendly or stagflationary. Weak growth with sticky inflation is the harder regime.
                    </p>
                  </CardContent>
                </Card>
                <Card style={cardStyle}>
                  <CardContent className="p-5">
                    <h3 className="text-lg font-semibold">Context-Only Series</h3>
                    <p className="mt-2 text-sm leading-6" style={{ color: ui.textSecondary }}>
                      GDP, GDPNow, M2, monetary base, participation, and USREC should explain or validate the story. They should not override faster financial, labor, and activity signals.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : null}

          {activeSection === "operations" ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Card style={cardStyle}>
                  <CardContent className="p-5">
                    <h3 className="text-lg font-semibold">Data Refresh</h3>
                    <p className="mt-2 text-sm leading-6" style={{ color: ui.textSecondary }}>
                      The dashboard reads series already stored in Blob. After adding a new FRED series, run Update FRED Data for the full catalog. The scheduled backend timer still handles routine refreshes.
                    </p>
                    <div className="mt-4 rounded-lg p-3 text-sm" style={softCardStyle}>
                      Full refresh: all configured economic catalog series.
                    </div>
                  </CardContent>
                </Card>
                <Card style={cardStyle}>
                  <CardContent className="p-5">
                    <h3 className="text-lg font-semibold">Charting</h3>
                    <p className="mt-2 text-sm leading-6" style={{ color: ui.textSecondary }}>
                      Indicator modals try to load full Blob history first. If the full-history endpoint is unavailable, the chart falls back to preloaded dashboard history so the modal remains usable.
                    </p>
                    <div className="mt-4 rounded-lg p-3 text-sm" style={softCardStyle}>
                      Time windows are YTD, 1Y, 3Y, 5Y, 10Y, and MAX. Quarterly series use wider freshness windows than daily or weekly series.
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card style={cardStyle}>
                <CardContent className="p-5">
                  <h3 className="text-lg font-semibold">Freshness Windows</h3>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Daily</div>
                      <div className="mt-1 text-sm" style={{ color: ui.textSecondary }}>Aging after 4 days; stale after 10 days.</div>
                    </div>
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Weekly</div>
                      <div className="mt-1 text-sm" style={{ color: ui.textSecondary }}>Aging after 10 days; stale after 21 days.</div>
                    </div>
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Monthly</div>
                      <div className="mt-1 text-sm" style={{ color: ui.textSecondary }}>Aging after 45 days; stale after 90 days.</div>
                    </div>
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Quarterly</div>
                      <div className="mt-1 text-sm" style={{ color: ui.textSecondary }}>Aging after 105 days; stale after 150 days.</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card style={cardStyle}>
                <CardContent className="p-5">
                  <h3 className="text-lg font-semibold">Model Governance</h3>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Certification Layer</div>
                      <div className="mt-1 text-sm leading-6" style={{ color: ui.textSecondary }}>Walk-forward validation is the production certification layer. Full-history backtests are useful diagnostics, but they are not the sole sign-off test.</div>
                    </div>
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Operating Model</div>
                      <div className="mt-1 text-sm leading-6" style={{ color: ui.textSecondary }}>The model is a continuous macro deterioration probability engine. It should not be presented as a binary recession classifier.</div>
                    </div>
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Sentinel vs Watch</div>
                      <div className="mt-1 text-sm leading-6" style={{ color: ui.textSecondary }}>Sentinel is early radar only. Watch, Caution, and Severe are the formal CIO review bands.</div>
                    </div>
                    <div className="rounded-lg p-3" style={softCardStyle}>
                      <div className="text-xs uppercase tracking-wide" style={{ color: ui.textMuted }}>Execution Boundary</div>
                      <div className="mt-1 text-sm leading-6" style={{ color: ui.textSecondary }}>No dashboard state is a trade instruction. Execution, order staging, CIO approval, risk caps, and audit logging remain outside this dashboard and unchanged.</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="rounded-lg p-4" style={{ ...softCardStyle, border: "1px solid rgba(202, 138, 4, 0.35)" }}>
                <div className="font-semibold">Governance Note</div>
                <p className="mt-2 text-sm leading-6" style={{ color: ui.textSecondary }}>
                  This methodology explains macro conditions only. Execution, order staging, CIO approval, risk caps, and audit logging remain outside this dashboard and unchanged.
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const EconomicDashboard: React.FC = () => {
  const { userLoading } = useUser();
  const { theme } = useTheme();
  const chart = useMemo(() => getEconChartPalette(theme), [theme]);
  const [overview, setOverview] = useState<EconOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndicator, setSelectedIndicator] = useState<EconIndicator | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<HistoryTimeframe>("5Y");
  const [historyCache, setHistoryCache] = useState<Record<string, HistoryCacheEntry>>({});
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [refreshingFred, setRefreshingFred] = useState<null | "all" | "selected">(null);
  const [refreshStatus, setRefreshStatus] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [methodologyOpen, setMethodologyOpen] = useState(false);

  const loadOverview = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${ECON_API_BASE_URL}/econ/overview`, {
        signal,
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error(`econ overview ${response.status}`);
      }
      const payload: EconOverview = await response.json();
      setOverview(normalizeOverviewLabels(payload));
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        setError(err?.message || "Failed to load economics overview");
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (userLoading) return;

    const controller = new AbortController();
    loadOverview(controller.signal);

    return () => controller.abort();
  }, [loadOverview, userLoading]);

  const runFredRefresh = useCallback(async (seriesIds?: string[]) => {
    const selectedSeries = (seriesIds ?? []).map((item) => item.trim().toUpperCase()).filter(Boolean);
    const mode: "all" | "selected" = selectedSeries.length ? "selected" : "all";

    try {
      setRefreshingFred(mode);
      setRefreshError(null);
      setRefreshStatus(mode === "selected" ? `Refreshing ${selectedSeries.join(", ")}…` : "Refreshing all configured FRED series…");

      const params = new URLSearchParams();
      if (selectedSeries.length) {
        params.set("series", selectedSeries.join(","));
      }
      const query = params.toString();
      const baseUrl = `${ECON_API_BASE_URL}/update_fred_data_now${query ? `?${query}` : ""}`;
      const response = await fetch(appendFunctionKey(baseUrl), {
        method: "POST",
        cache: "no-store",
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("FRED refresh returned 401. Deploy the anonymous refresh auth patch or set REACT_APP_FUNC_KEY for this dashboard build.");
        }
        throw new Error(`FRED refresh ${response.status}`);
      }

      const payload = await response.json();
      setRefreshStatus(refreshSummaryText(payload));
      setHistoryCache({});
      await loadOverview();
    } catch (err: any) {
      setRefreshError(err?.message || "Failed to refresh FRED data");
    } finally {
      setRefreshingFred(null);
    }
  }, [loadOverview]);

  const selectedIndicatorHistory = useMemo(() => {
    if (!selectedIndicator) return null;
    const cacheEntry = historyCache[historyCacheKey(selectedIndicator.series_id, selectedTimeframe)];
    if (cacheEntry) return cacheEntry.history;

    const fallbackHistory = overview?.histories?.[selectedIndicator.series_id] ?? null;
    return windowHistoryForTimeframe(fallbackHistory, selectedTimeframe, overview?.as_of || selectedIndicator.date);
  }, [historyCache, overview, selectedIndicator, selectedTimeframe]);

  const openIndicatorHistory = useCallback((indicator: EconIndicator) => {
    setSelectedIndicator(indicator);
    setSelectedTimeframe("5Y");
    setHistoryError(null);
  }, []);

  const closeIndicatorHistory = useCallback(() => {
    setSelectedIndicator(null);
    setHistoryLoading(false);
    setHistoryError(null);
  }, []);

  useEffect(() => {
    if (!selectedIndicator) {
      setHistoryLoading(false);
      setHistoryError(null);
      return;
    }

    const seriesId = selectedIndicator.series_id;
    const cacheKey = historyCacheKey(seriesId, selectedTimeframe);
    if (historyCache[cacheKey]) {
      setHistoryLoading(false);
      setHistoryError(null);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        setHistoryLoading(true);
        setHistoryError(null);

        const params = new URLSearchParams();
        params.set("max_points", selectedTimeframe === "MAX" ? "10000" : "2000");
        const startDate = timeframeStartDate(selectedTimeframe, overview?.as_of || selectedIndicator.date);
        if (startDate) {
          params.set("start", startDate);
        }

        const historyUrl = `${ECON_API_BASE_URL}/econ/history/${encodeURIComponent(seriesId)}?${params.toString()}`;
        const response = await fetch(appendFunctionKey(historyUrl), {
          signal: controller.signal,
          cache: "no-store",
        });
        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("Live full-history endpoint returned 401; showing dashboard snapshot history instead.");
          }
          throw new Error(`indicator history ${response.status}`);
        }
        const payload: EconHistory = await response.json();
        payload.window_label = timeframeWindowLabel(selectedTimeframe);
        setHistoryCache((current) => ({
          ...current,
          [cacheKey]: {
            fetchedAt: new Date().toISOString(),
            history: payload,
          },
        }));
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          if (overview?.histories?.[seriesId]) {
            setHistoryError(err?.message || "Live blob history was unavailable; showing dashboard snapshot history instead.");
          } else {
            setHistoryError(err?.message || "Failed to load indicator history");
          }
        }
      } finally {
        if (!controller.signal.aborted) {
          setHistoryLoading(false);
        }
      }
    })();

    return () => controller.abort();
  }, [historyCache, overview, selectedIndicator, selectedTimeframe]);

  const groupedIndicators = useMemo(() => {
    const groups: Record<string, EconIndicator[]> = {};
    for (const item of overview?.indicators ?? []) {
      const groupKey = item.theme || item.type || "Other";
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(item);
    }
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [overview]);

  const regimeTone = useMemo<ScoreBucket>(() => {
    const label = overview?.regime.current.label || "Cautionary";
    if (label === "Expansionary") return "expansionary";
    if (label === "Recessionary") return "recessionary";
    return "cautionary";
  }, [overview]);

  const coreCpiIndicator = useMemo(() => {
    return (overview?.indicators ?? []).find((item) => item.series_id === "CPILFESL") ?? null;
  }, [overview]);

  const indicatorById = useMemo(() => {
    const entries = (overview?.indicators ?? []).map((item) => [item.series_id, item] as const);
    return new Map(entries);
  }, [overview]);

  const gdpIndicator = indicatorById.get("GDPC1") ?? null;
  const gdpNowIndicator = indicatorById.get("GDPNOW") ?? null;
  const sahmIndicator = indicatorById.get("SAHMREALTIME") ?? null;
  const dimensionEntries = useMemo(() => {
    const preferredOrder = [
      "Growth Pulse",
      "Labor Stress",
      "Inflation / Fed Pressure",
      "Financial Conditions / Liquidity",
      "Consumer / Housing",
      "GDP / Nowcast Context",
    ];
    const dimensions = overview?.dimensions ?? {};
    return preferredOrder
      .map((key) => dimensions[key])
      .filter(Boolean)
      .concat(
        Object.entries(dimensions)
          .filter(([key]) => !preferredOrder.includes(key))
          .map(([, value]) => value)
      ) as EconDimension[];
  }, [overview]);

  // Section pills plus one per indicator-monitor theme. The theme pills are built
  // from groupedIndicators itself, so a renamed or newly added theme cannot leave
  // a pill pointing at an id no element carries — the exact defect this nav
  // shipped once on the Risk page. Dimensions is conditional on the same test
  // that decides whether its section renders at all.
  const econNavItems = useMemo(() => {
    const sections: Array<{ id: string; label: string }> = [
      { id: "econ-sec-overview", label: "Overview" },
      { id: "econ-sec-narrative", label: "Narrative & Risk" },
      ...(dimensionEntries.length ? [{ id: "econ-sec-dimensions", label: "Dimensions" }] : []),
      { id: "econ-sec-trends", label: "Trends" },
      { id: "econ-sec-playbook", label: "Playbook" },
      { id: "econ-sec-breadth", label: "Breadth" },
      { id: "econ-sec-drivers", label: "Supports & Watch" },
      { id: "econ-sec-indicators", label: "Indicators" },
    ];
    return sections.concat(
      groupedIndicators.map(([groupName]) => ({
        id: groupAnchorId(groupName),
        label: groupNavLabel(groupName),
      }))
    );
  }, [groupedIndicators, dimensionEntries]);

  const freshnessSummary = useMemo<LocalFreshnessSummary | null>(() => {
    if (!overview) return null;
    return computeCadenceAwareFreshness(overview.indicators, overview.histories, overview.freshness);
  }, [overview]);

  const freshnessTone = useMemo<ScoreBucket>(() => {
    if (!freshnessSummary) return "expansionary";
    if (freshnessSummary.status === "stale") return "recessionary";
    if (freshnessSummary.status === "aging") return "cautionary";
    return "expansionary";
  }, [freshnessSummary]);

  const narrative = useMemo(() => {
    return {
      headline: overview?.narrative?.headline?.trim() || "",
      body: overview?.narrative?.body?.trim() || "",
      portfolio: overview?.narrative?.portfolio?.trim() || "",
    };
  }, [overview]);

  // Structured view of the same facts the engine writes into narrative.body. Series ids and
  // selection rules mirror HelionBackend/econ_overview/__init__.py::_macro_narrative — the
  // "Dimension map" clause is intentionally dropped here because the Macro Dimension Map card
  // below renders it in full.
  const narrativeDetail = useMemo(() => {
    const pick = (ids: string[], limit: number, predicate?: (item: EconIndicator) => boolean) => {
      const out: EconIndicator[] = [];
      for (const id of ids) {
        const item = indicatorById.get(id);
        if (!item || item.value == null || Number.isNaN(Number(item.value))) continue;
        if (predicate && !predicate(item)) continue;
        out.push(item);
        if (out.length >= limit) break;
      }
      return out;
    };

    const inflation = pick(["PCEPILFE", "PCEPI", "CPILFESL", "CPIAUCSL", "PPIACO", "AHETPI"], 3, (item) => item.score <= 1);
    const growth = pick(["CFNAIMA3", "INDPRO", "PAYEMS", "GDPC1", "GDPNOW"], 4);
    const housing = pick(["CSUSHPINSA_REAL_YOY", "CSUSHPINSA_3M_ANN"], 2);
    const housingDate = housing.map((item) => item.date).filter(Boolean).sort().slice(-1)[0] ?? null;

    const trigger = overview?.early_warning?.latest_trigger ?? null;
    const recovery = overview?.early_warning?.latest_recovery ?? null;
    const triggerInForce = Boolean(trigger) && (!recovery || (trigger?.date ?? "") >= (recovery?.date ?? ""));
    const signal = triggerInForce && trigger
      ? { tone: "recessionary" as ScoreBucket, text: `Early-warning trigger in force since ${fmtLocalDate(trigger.date)}` }
      : recovery
        ? { tone: "expansionary" as ScoreBucket, text: `Latest recovery signal ${fmtLocalDate(recovery.date)}` }
        : null;

    const supports = overview?.drivers.supports ?? [];
    const risks = overview?.drivers.risks ?? [];
    const readThrough = narrative.portfolio.replace(/^Portfolio read-through:\s*/i, "");

    return {
      supports: supports.slice(0, 3),
      risks: risks.slice(0, 3),
      moreSupports: Math.max(supports.length - 3, 0),
      moreRisks: Math.max(risks.length - 3, 0),
      inflation,
      growth,
      housing,
      housingDate,
      signal,
      readThrough: readThrough ? readThrough.charAt(0).toUpperCase() + readThrough.slice(1) : "",
      hasStructure: Boolean(supports.length || risks.length || inflation.length || growth.length || housing.length),
    };
  }, [overview, indicatorById, narrative.portfolio]);

  const operatingThresholds = useMemo<OperatingThresholds>(() => {
    const earlyWarning = overview?.early_warning;
    const bandLower = (key: string, fallback: number) => {
      const lower = toFraction(earlyWarning?.operating_bands?.[key]?.lower);
      return lower ?? fallback;
    };

    return {
      sentinel: bandLower("sentinel", 0.25),
      monitor: bandLower("monitor", toFraction(earlyWarning?.thresholds?.recovery) ?? 0.35),
      watch: bandLower("watch", 0.45),
      caution: bandLower("caution", 0.55),
      severe: bandLower("severe", 0.68),
    };
  }, [overview]);

  const dashboardRisk = useMemo(() => {
    return buildDashboardRiskPresentation({
      rawSignal: overview?.early_warning?.signal,
      radarState: overview?.early_warning?.radar_state,
      weakeningProb: overview?.regime.current.weakening_prob ?? overview?.early_warning?.weakening_prob,
      rollingWeakeningProb: overview?.early_warning?.rolling_weakening_prob,
      thresholds: {
        sentinel: operatingThresholds.sentinel,
        recovery: operatingThresholds.monitor,
        watch: operatingThresholds.watch,
        caution: operatingThresholds.caution,
        severe: operatingThresholds.severe,
      },
      delta3m: overview?.early_warning?.delta_3m ?? overview?.early_warning?.latest_trigger?.delta_3m ?? null,
    });
  }, [overview, operatingThresholds]);

  const weakeningChartValues = useMemo(() => {
    return overview?.regime.history.weakening_prob.map((value) => value * 100) ?? [];
  }, [overview]);

  const rollingWeakeningChartValues = useMemo(() => movingAverage(weakeningChartValues, 3), [weakeningChartValues]);

  const currentDashboardProbability = toFraction(overview?.early_warning?.rolling_weakening_prob ?? overview?.regime.current.weakening_prob);
  const currentDashboardBand = bandForProbability(currentDashboardProbability, operatingThresholds);
  const currentReviewAction = reviewActionForBand(currentDashboardBand);
  const latestRawWeakeningPct = latestNumeric(weakeningChartValues);
  const latestRollingWeakeningPct = latestNumeric(rollingWeakeningChartValues);
  const rawWeakeningMonthDeltaPct = latestNumericDelta(weakeningChartValues);
  const rollingWeakeningMonthDeltaPct = latestNumericDelta(rollingWeakeningChartValues);
  const persistenceCounts = useMemo(() => ({
    sentinel: consecutivePeriodsAtOrAbove(rollingWeakeningChartValues, operatingThresholds.sentinel * 100),
    monitor: consecutivePeriodsAtOrAbove(rollingWeakeningChartValues, operatingThresholds.monitor * 100),
    watch: consecutivePeriodsAtOrAbove(rollingWeakeningChartValues, operatingThresholds.watch * 100),
    caution: consecutivePeriodsAtOrAbove(rollingWeakeningChartValues, operatingThresholds.caution * 100),
    severe: consecutivePeriodsAtOrAbove(rollingWeakeningChartValues, operatingThresholds.severe * 100),
  }), [rollingWeakeningChartValues, operatingThresholds]);
  const financialConditionsDimension = dimensionEntries.find((item) => item.theme === "Financial Conditions / Liquidity" || item.label === "Financial Conditions / Liquidity");
  const laborStressDimension = dimensionEntries.find((item) => item.theme === "Labor Stress" || item.label === "Labor Stress");
  const inflationPressureDimension = dimensionEntries.find((item) => item.theme === "Inflation / Fed Pressure" || item.label === "Inflation / Fed Pressure");
  const consumerHousingDimension = dimensionEntries.find((item) => item.theme === "Consumer / Housing" || item.label === "Consumer / Housing");
  const stressBreadth = overview?.summary.recessionary ?? 0;
  const cautionBreadth = overview?.summary.cautionary ?? 0;
  const supportBreadth = overview?.summary.expansionary ?? 0;
  const totalBreadth = supportBreadth + cautionBreadth + stressBreadth;
  const confirmationSummary = [
    financialConditionsDimension ? `Financial conditions: ${financialConditionsDimension.status}` : null,
    laborStressDimension ? `Labor: ${laborStressDimension.status}` : null,
    inflationPressureDimension ? `Inflation/Fed pressure: ${inflationPressureDimension.status}` : null,
    consumerHousingDimension ? `Consumer/housing: ${consumerHousingDimension.status}` : null,
  ].filter(Boolean).join(" • ");
  const latestEscalationLabel = overview?.early_warning?.latest_trigger
    ? `${fmtLocalDate(overview.early_warning.latest_trigger.date)} at ${pct(overview.early_warning.latest_trigger.weakening_prob)}`
    : "No escalation on file";
  const latestImprovementLabel = overview?.early_warning?.latest_recovery
    ? `${fmtLocalDate(overview.early_warning.latest_recovery.date)} at ${pct(overview.early_warning.latest_recovery.weakening_prob)}`
    : "No recovery on file";

  // Advisor-facing export. Deliberately NOT a dump of this page: see the design
  // note in src/lib/econSnapshot.ts for why the engine narrative and the
  // operational panels are excluded, and why probabilities are published as
  // words rather than percentages.
  const handleDownloadSnapshot = () => {
    if (!overview) return;
    downloadEconSnapshot({
      asOf: overview.as_of,
      generatedAt: new Date().toISOString(),
      regimeLabel: overview.regime.current.label,
      regimeIsFallback: Boolean((overview.regime as any)?.is_fallback),
      riskBand: currentDashboardBand,
      riskTrend: trendDescriptor(
        overview.early_warning?.delta_3m ?? overview.early_warning?.latest_trigger?.delta_3m ?? null
      ),
      dimensions: dimensionEntries.map((dim) => ({ label: dim.label, status: dim.status })),
      supports: overview.drivers.supports,
      risks: overview.drivers.risks,
      indicators: overview.indicators,
      regimeHistory: overview.regime.history,
      // Already in percent and index-aligned to regime.history.dates. Passed in
      // rather than recomputed inside the exporter so the CSV and the chart on
      // this page are literally the same numbers.
      weakeningRolling3mPct: rollingWeakeningChartValues,
      bands: operatingThresholds,
      histories: overview.histories,
      formatValue: (item) => formatIndicatorValue(item),
      formatDate: (value) => fmtLocalDate(value),
    });
  };

  if (userLoading) {
    return (
      <Layout>
        <div className="py-6">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-6 space-y-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">🏛️ Economics</h1>
            <p className="mt-1 text-sm" style={{ color: ui.textSecondary }}>
              Institutional macro briefing layer for regime, inflation, early warning, and portfolio posture.
              {overview?.ml_status?.model_trained_at_utc ? ` Latest model train: ${fmtLocalDate(overview.ml_status.model_trained_at_utc)}.` : ""}
              {overview?.ml_status?.scored_at_utc ? ` Latest score refresh: ${fmtLocalDate(overview.ml_status.scored_at_utc)}.` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleDownloadSnapshot}
              disabled={!overview}
              title="Download a curated, advisor-facing Economic Snapshot as Markdown"
              className="rounded-md px-3 py-2 text-sm font-semibold transition disabled:opacity-50"
              style={{
                backgroundColor: "rgba(16, 185, 129, 0.14)",
                color: "#34d399",
                border: "1px solid rgba(52, 211, 153, 0.32)",
              }}
            >
              Export Snapshot (.md)
            </button>
            <button
              type="button"
              onClick={() => setMethodologyOpen(true)}
              className="rounded-md px-3 py-2 text-sm font-semibold transition"
              style={{
                backgroundColor: "rgba(14, 165, 233, 0.14)",
                color: "#38bdf8",
                border: "1px solid rgba(56, 189, 248, 0.32)",
              }}
            >
              Methodology
            </button>
            <button
              type="button"
              onClick={() => runFredRefresh()}
              disabled={Boolean(refreshingFred)}
              className="rounded-md px-3 py-2 text-sm font-semibold transition disabled:opacity-60"
              style={{
                backgroundColor: ui.sectionBg,
                color: ui.textPrimary,
                border: `1px solid ${ui.cardBorder}`,
              }}
            >
              {refreshingFred === "all" ? "Refreshing…" : "Update FRED Data"}
            </button>
          </div>
        </div>

        {refreshError ? (
          <Card style={cardStyle}>
            <CardContent className="p-3 text-sm text-down">{refreshError}</CardContent>
          </Card>
        ) : refreshStatus ? (
          <Card style={cardStyle}>
            <CardContent className="p-3 text-sm" style={{ color: ui.textSecondary }}>{refreshStatus}</CardContent>
          </Card>
        ) : null}

        <Tabs defaultValue="current" className="w-full">
          <TabsList>
            <TabsTrigger value="current">Current Model</TabsTrigger>
            <TabsTrigger value="ml">Machine Learning</TabsTrigger>
          </TabsList>

          <TabsContent value="current" className="mt-4">
            {loading ? (
              <Card style={cardStyle}>
                <CardContent className="p-6 text-center" style={{ color: ui.textMuted }}>
                  Loading economics overview...
                </CardContent>
              </Card>
            ) : error ? (
              <Card style={cardStyle}>
                <CardContent className="p-6 text-center text-down">
                  Error loading economics dashboard: {error}
                </CardContent>
              </Card>
            ) : overview ? (
              <div className="space-y-5">
                <QuickNav items={econNavItems} />
                {overview.missing_indicators && overview.missing_indicators.length > 0 && (
                  <div className="rounded-lg border px-4 py-3 text-sm" style={{ borderColor: "rgba(245, 158, 11, 0.5)", backgroundColor: "rgba(245, 158, 11, 0.08)", color: "#fbbf24" }}>
                    ⚠ {overview.missing_indicators.length} indicator feed(s) failed to load and are omitted from the board:{" "}
                    {overview.missing_indicators.map((m) => m.label || m.series_id).filter(Boolean).join(", ")}
                  </div>
                )}
                <section id="econ-sec-overview" className="scroll-mt-20">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-7 gap-4">
                  <MetricCard
                    title="Current Regime"
                    value={overview.regime.current.label}
                    subtitle={
                      (overview.regime as any)?.is_fallback
                        ? `⚠ Heuristic fallback — trained ML model unavailable • As of ${fmtLocalDate(overview.regime.current.date || overview.as_of)}`
                        : `As of ${fmtLocalDate(overview.regime.current.date || overview.as_of)}`
                    }
                    tone={(overview.regime as any)?.is_fallback ? "recessionary" : regimeTone}
                  />
                  <MetricCard
                    title="Recessionary Regime"
                    value={pct(overview.regime.current.recessionary)}
                    subtitle={`Secondary regime probability • Δ vs prior: ${deltaPct(overview.regime.current.delta_vs_prior.recessionary)}`}
                    tone="recessionary"
                  />
                  <MetricCard
                    title="Caution Probability"
                    value={pct(overview.regime.current.cautionary)}
                    subtitle={`Δ vs prior: ${deltaPct(overview.regime.current.delta_vs_prior.cautionary)}`}
                    tone="cautionary"
                  />
                  <MetricCard
                    title="Core CPI YoY"
                    value={formatIndicatorValue(coreCpiIndicator)}
                    subtitle={coreCpiIndicator ? `Latest Core CPI date: ${fmtLocalDate(coreCpiIndicator.date)}` : "Core CPI snapshot unavailable"}
                    tone="cautionary"
                  />
                  <MetricCard
                    title="Real GDP QoQ"
                    value={formatIndicatorValue(gdpIndicator)}
                    subtitle={gdpIndicator ? `Context only • ${fmtLocalDate(gdpIndicator.date)}` : "GDP context unavailable"}
                    tone={gdpIndicator ? (gdpIndicator.score >= 2 ? "expansionary" : gdpIndicator.score === 1 ? "cautionary" : "recessionary") : undefined}
                  />
                  <MetricCard
                    title="Sahm Rule"
                    value={formatIndicatorValue(sahmIndicator)}
                    subtitle={sahmIndicator ? `Labor stress • ${fmtLocalDate(sahmIndicator.date)}` : "Sahm signal unavailable"}
                    tone={sahmIndicator ? (sahmIndicator.score >= 2 ? "expansionary" : sahmIndicator.score === 1 ? "cautionary" : "recessionary") : undefined}
                  />
                  <MetricCard
                    title="Data Freshness (feed quality)"
                    value={freshnessSummary ? titleCase(freshnessSummary.status) : "—"}
                    subtitle={
                      freshnessSummary
                        ? `Data-pipeline check, not macro risk • Aging: ${freshnessSummary.agingCount} • Stale: ${freshnessSummary.staleCount} • Newest ${fmtLocalDate(freshnessSummary.latestDate)} • Oldest ${fmtLocalDate(freshnessSummary.earliestDate)}`
                        : "Freshness unavailable"
                    }
                    tone={freshnessTone}
                  />
                </div>
                </section>

                <section id="econ-sec-narrative" className="scroll-mt-20">
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                  <Card style={cardStyle} className="xl:col-span-2">
                    <CardContent className="p-5 space-y-4">
                      <div>
                        <h2 className="text-lg font-semibold">Macro Narrative</h2>
                        <p className="text-sm mt-1 leading-6" style={{ color: ui.textSecondary }}>{narrative.headline}</p>
                      </div>

                      {narrativeDetail.hasStructure ? (
                        <>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <NarrativeSection
                              title="Supporting"
                              hint={narrativeDetail.moreSupports ? `+${narrativeDetail.moreSupports} more below` : undefined}
                            >
                              <NarrativeChips items={narrativeDetail.supports} tone="expansionary" emptyText="No broad-based positives" />
                            </NarrativeSection>
                            <NarrativeSection
                              title="Watching"
                              hint={narrativeDetail.moreRisks ? `+${narrativeDetail.moreRisks} more below` : undefined}
                            >
                              <NarrativeChips items={narrativeDetail.risks} emptyText="No immediate macro stress points" />
                            </NarrativeSection>
                          </div>

                          {narrativeDetail.inflation.length ? (
                            <NarrativeSection title="Inflation / Fed pressure" hint="still bears watching">
                              <NarrativeStatGrid items={narrativeDetail.inflation} tone="cautionary" />
                            </NarrativeSection>
                          ) : null}

                          {narrativeDetail.growth.length ? (
                            <NarrativeSection title="Growth context">
                              <NarrativeStatGrid items={narrativeDetail.growth} />
                            </NarrativeSection>
                          ) : null}

                          {narrativeDetail.housing.length ? (
                            <NarrativeSection
                              title="Housing wealth context"
                              hint={narrativeDetail.housingDate ? `as of ${fmtLocalDate(narrativeDetail.housingDate)}` : undefined}
                            >
                              <NarrativeStatGrid items={narrativeDetail.housing} />
                              <div className="mt-2 text-xs leading-5" style={{ color: ui.textMuted }}>
                                Lagged structural context indicators. Excluded from economic AML training.
                              </div>
                            </NarrativeSection>
                          ) : null}

                          {narrativeDetail.signal ? (
                            <div
                              className="text-xs px-2.5 py-1 rounded-full inline-block"
                              style={{
                                backgroundColor: bucketTone[narrativeDetail.signal.tone].chipBg,
                                color: bucketTone[narrativeDetail.signal.tone].text,
                                border: `1px solid ${bucketTone[narrativeDetail.signal.tone].border}`,
                              }}
                            >
                              {narrativeDetail.signal.text}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <p className="text-sm leading-6" style={{ color: ui.textSecondary }}>{narrative.body}</p>
                      )}

                      {narrativeDetail.readThrough ? (
                        <div className="rounded-lg p-3" style={softCardStyle}>
                          <div className="text-xs uppercase tracking-wide font-medium mb-1" style={{ color: ui.textMuted }}>Portfolio read-through</div>
                          <div className="text-sm leading-6" style={{ color: ui.textSecondary }}>{narrativeDetail.readThrough}</div>
                        </div>
                      ) : null}

                      {narrativeDetail.hasStructure && narrative.body ? (
                        <details className="text-xs">
                          <summary className="cursor-pointer" style={{ color: ui.textMuted }}>
                            Full narrative text (as generated by the economics engine)
                          </summary>
                          <p className="mt-2 leading-5" style={{ color: ui.textSecondary }}>{narrative.body}</p>
                          <p className="mt-2 leading-5" style={{ color: ui.textSecondary }}>{narrative.portfolio}</p>
                        </details>
                      ) : null}
                    </CardContent>
                  </Card>

                  <Card style={cardStyle}>
                    <CardContent className="p-5 space-y-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h2 className="text-lg font-semibold">Macro Risk Status</h2>
                          <p className="text-sm" style={{ color: ui.textSecondary }}>
                            UI-facing interpretation layer for the dashboard, built from the early-warning engine.
                          </p>
                        </div>
                        <span
                          className="text-xs px-2.5 py-1 rounded-full whitespace-nowrap"
                          style={{
                            backgroundColor: bucketTone[dashboardRisk.tone].chipBg,
                            color: bucketTone[dashboardRisk.tone].text,
                            border: `1px solid ${bucketTone[dashboardRisk.tone].border}`,
                          }}
                        >
                          {dashboardRisk.badgeText}
                        </span>
                      </div>

                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Current Read</div>
                        <div className="text-lg font-semibold" style={{ color: bucketTone[dashboardRisk.tone].text }}>
                          {dashboardRisk.label}
                        </div>
                        <div className="mt-1 text-sm" style={{ color: ui.textSecondary }}>{dashboardRisk.summary}</div>
                        <div className="mt-2 text-xs leading-5" style={{ color: ui.textMuted }}>{dashboardRisk.detail}</div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="rounded-lg p-3" style={softCardStyle}>
                          <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Weakening</div>
                          <div className="text-base font-semibold">{pct(overview.early_warning?.rolling_weakening_prob ?? overview.regime.current.weakening_prob)}</div>
                          <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Rolling dashboard probability</div>
                        </div>
                        <div className="rounded-lg p-3" style={softCardStyle}>
                          <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Radar State</div>
                          <div className="text-base font-semibold">{titleCase((overview.early_warning?.radar_state || dashboardRisk.status).replace(/_/g, " "))}</div>
                          <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Sentinel → Monitor → Watch bands</div>
                        </div>
                        <div className="rounded-lg p-3" style={softCardStyle}>
                          <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>3-Month Trend</div>
                          <div className="text-base font-semibold">{deltaPct(overview.early_warning?.delta_3m ?? overview.early_warning?.latest_trigger?.delta_3m ?? 0)}</div>
                          <div className="text-xs mt-1" style={{ color: ui.textMuted }}>Direction of change</div>
                        </div>
                      </div>

                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Latest Escalation</div>
                        {overview.early_warning?.latest_trigger ? (
                          <div className="text-sm" style={{ color: ui.textSecondary }}>
                            <div>{fmtLocalDate(overview.early_warning.latest_trigger.date)}</div>
                            <div>Weakening: {pct(overview.early_warning.latest_trigger.weakening_prob)}</div>
                            <div>Δ 3m: {deltaPct(overview.early_warning.latest_trigger.delta_3m)}</div>
                          </div>
                        ) : (
                          <div className="text-sm" style={{ color: ui.textMuted }}>No active escalation on file.</div>
                        )}
                      </div>

                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Latest Improvement</div>
                        {overview.early_warning?.latest_recovery ? (
                          <div className="text-sm" style={{ color: ui.textSecondary }}>
                            <div>{fmtLocalDate(overview.early_warning.latest_recovery.date)}</div>
                            <div>Weakening: {pct(overview.early_warning.latest_recovery.weakening_prob)}</div>
                            <div>Δ 3m: {deltaPct(overview.early_warning.latest_recovery.delta_3m)}</div>
                          </div>
                        ) : (
                          <div className="text-sm" style={{ color: ui.textMuted }}>No improvement event has been logged in the published history yet.</div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
                </section>

                {dimensionEntries.length ? (
                  <section id="econ-sec-dimensions" className="scroll-mt-20">
                  <Card style={cardStyle}>
                    <CardContent className="p-5">
                      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
                        <div>
                          <h2 className="text-lg font-semibold">Macro Dimension Map</h2>
                          <p className="text-sm" style={{ color: ui.textSecondary }}>Growth, labor, inflation/Fed pressure, and financial conditions separated so GDP remains context—not the primary driver.</p>
                        </div>
                        <div className="text-xs" style={{ color: ui.textMuted }}>
                          {gdpNowIndicator ? `GDPNow: ${formatIndicatorValue(gdpNowIndicator)} • ${fmtLocalDate(gdpNowIndicator.date)}` : "GDPNow context unavailable"}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {dimensionEntries.map((dimension) => {
                          const tone: ScoreBucket = dimension.status === "Supportive"
                            ? "expansionary"
                            : dimension.status === "Stress"
                              ? "recessionary"
                              : "cautionary";
                          return (
                            <div key={dimension.theme} className="rounded-lg p-4" style={softCardStyle}>
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="font-semibold">{dimension.label}</div>
                                  <div className="text-xs mt-1" style={{ color: ui.textMuted }}>
                                    Avg score {dimension.average_score == null ? "—" : dimension.average_score.toFixed(2)} • {dimension.indicator_count} scored{dimension.context_count ? ` • ${dimension.context_count} context` : ""}
                                  </div>
                                </div>
                                <span
                                  className="text-xs px-2 py-1 rounded-full whitespace-nowrap"
                                  style={{
                                    backgroundColor: bucketTone[tone].chipBg,
                                    color: bucketTone[tone].text,
                                    border: `1px solid ${bucketTone[tone].border}`,
                                  }}
                                >
                                  {dimension.status}
                                </span>
                              </div>
                              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                                <div><span style={{ color: bucketTone.expansionary.text }}>{dimension.counts.expansionary}</span> supportive</div>
                                <div><span style={{ color: bucketTone.cautionary.text }}>{dimension.counts.cautionary}</span> caution</div>
                                <div><span style={{ color: bucketTone.recessionary.text }}>{dimension.counts.recessionary}</span> stress</div>
                              </div>
                              {dimension.watch_items?.length ? (
                                <div className="mt-3 text-xs leading-5" style={{ color: ui.textSecondary }}>
                                  Watch: {dimension.watch_items.slice(0, 2).map((item) => item.label).join(", ")}
                                </div>
                              ) : dimension.support_items?.length ? (
                                <div className="mt-3 text-xs leading-5" style={{ color: ui.textSecondary }}>
                                  Support: {dimension.support_items.slice(0, 2).map((item) => item.label).join(", ")}
                                </div>
                              ) : null}
                              {dimension.context_items?.length ? (
                                <div className="mt-2 text-xs leading-5" style={{ color: ui.textMuted }}>
                                  Context only: {dimension.context_items.slice(0, 2).map((item) => item.label).join(", ")}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                  </section>
                ) : null}

                <section id="econ-sec-trends" className="scroll-mt-20">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <PlotCard
                    title="Regime Probability Trend"
                    subtitle="Monthly probabilities over the latest history window"
                    dates={overview.regime.history.dates}
                    valueSuffix="%"
                    series={[
                      { name: "Expansionary", values: overview.regime.history.expansionary.map((v) => v * 100), color: chart.expansionary },
                      { name: "Cautionary", values: overview.regime.history.cautionary.map((v) => v * 100), color: chart.cautionary },
                      { name: "Recessionary", values: overview.regime.history.recessionary.map((v) => v * 100), color: chart.recessionary },
                    ]}
                  />
                  <PlotCard
                    title="Weakening Probability"
                    subtitle="Raw monthly probability plus 3-month rolling state line. Use the rolling line for current posture; use raw spikes as radar."
                    dates={overview.regime.history.dates}
                    valueSuffix="%"
                    series={[
                      { name: "Raw monthly weakening", values: weakeningChartValues, color: chart.rawWeakening, width: 2.25 },
                      { name: "3M rolling state", values: rollingWeakeningChartValues, color: chart.rollingWeakening, width: 3 },
                    ]}
                    thresholdLines={[
                      { label: "Sentinel", value: operatingThresholds.sentinel * 100, color: chart.sentinel, labelColor: chart.sentinel, dash: "dot" },
                      { label: "Monitor", value: operatingThresholds.monitor * 100, color: chart.monitor, labelColor: chart.monitor, dash: "dot" },
                      { label: "Watch", value: operatingThresholds.watch * 100, color: chart.watch, labelColor: chart.watch, dash: "dash" },
                      { label: "Caution", value: operatingThresholds.caution * 100, color: chart.caution, labelColor: chart.caution, dash: "dash" },
                      { label: "Severe", value: operatingThresholds.severe * 100, color: chart.severe, labelColor: chart.severe, dash: "dash" },
                    ]}
                  />
                </div>
                </section>

                <section id="econ-sec-playbook" className="scroll-mt-20">
                <Card style={cardStyle}>
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div>
                        <h2 className="text-lg font-semibold">Signal Use & Review Playbook</h2>
                        <p className="text-sm" style={{ color: ui.textSecondary }}>
                          Monthly retraining can move the path in and out of bands. Use the chart to set review intensity; use persistence, trend, breadth, and drivers to decide how much weight to give it.
                        </p>
                      </div>
                      <span
                        className="text-xs px-2.5 py-1 rounded-full whitespace-nowrap"
                        style={{
                          backgroundColor: bucketTone[dashboardRisk.tone].chipBg,
                          color: bucketTone[dashboardRisk.tone].text,
                          border: `1px solid ${bucketTone[dashboardRisk.tone].border}`,
                        }}
                      >
                        Current band: {currentDashboardBand}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Current use</div>
                        <div className="text-base font-semibold">{currentReviewAction}</div>
                        <div className="mt-1 text-xs leading-5" style={{ color: ui.textMuted }}>
                          Rolling weakening: {pct(currentDashboardProbability)}. Current state uses the smoothed probability, not one raw monthly print.
                        </div>
                      </div>
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Trend quality</div>
                        <div className="text-base font-semibold">{trendDescriptor(overview.early_warning?.delta_3m)}</div>
                        <div className="mt-1 text-xs leading-5" style={{ color: ui.textMuted }}>
                          3M trend: {deltaPct(overview.early_warning?.delta_3m ?? 0)} • Latest rolling monthly change: {rollingWeakeningMonthDeltaPct == null ? "—" : `${rollingWeakeningMonthDeltaPct >= 0 ? "+" : ""}${rollingWeakeningMonthDeltaPct.toFixed(1)} pts`}.
                        </div>
                      </div>
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Persistence</div>
                        <div className="text-base font-semibold">{persistencePhrase(persistenceCounts.sentinel)} Sentinel</div>
                        <div className="mt-1 text-xs leading-5" style={{ color: ui.textMuted }}>
                          Monitor: {persistenceCounts.monitor}m • Watch: {persistenceCounts.watch}m • Caution: {persistenceCounts.caution}m • Severe: {persistenceCounts.severe}m.
                        </div>
                      </div>
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Breadth confirmation</div>
                        <div className="text-base font-semibold">{supportBreadth}/{totalBreadth || "—"} supportive</div>
                        <div className="mt-1 text-xs leading-5" style={{ color: ui.textMuted }}>
                          Caution: {cautionBreadth} • Stress: {stressBreadth}. {confirmationSummary || "Dimension confirmation unavailable."}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>What changed recently</div>
                        <div className="text-sm leading-6" style={{ color: ui.textSecondary }}>
                          <div>Latest raw weakening: {latestRawWeakeningPct == null ? "—" : `${latestRawWeakeningPct.toFixed(1)}%`} {rawWeakeningMonthDeltaPct == null ? "" : `(${rawWeakeningMonthDeltaPct >= 0 ? "+" : ""}${rawWeakeningMonthDeltaPct.toFixed(1)} pts vs prior)`}</div>
                          <div>Latest rolling state: {latestRollingWeakeningPct == null ? "—" : `${latestRollingWeakeningPct.toFixed(1)}%`} {rollingWeakeningMonthDeltaPct == null ? "" : `(${rollingWeakeningMonthDeltaPct >= 0 ? "+" : ""}${rollingWeakeningMonthDeltaPct.toFixed(1)} pts vs prior)`}</div>
                          <div>Latest escalation: {latestEscalationLabel} • Latest recovery: {latestImprovementLabel}</div>
                        </div>
                      </div>
                      <div className="rounded-lg p-3" style={softCardStyle}>
                        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: ui.textMuted }}>Operating rule</div>
                        <div className="text-sm leading-6" style={{ color: ui.textSecondary }}>
                          Single-month Sentinel/Monitor moves are radar events. Two-month persistence or a positive 3-month trend with broad bucket deterioration earns a CIO note. Watch starts formal CIO macro review. Caution/Severe require active posture review. No band crossing authorizes trading.
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                </section>

                <section id="econ-sec-breadth" className="scroll-mt-20">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <Card style={cardStyle}>
                    <CardContent className="p-4">
                      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Expansionary Breadth</div>
                      <div className="text-2xl font-semibold" style={{ color: bucketTone.expansionary.text }}>{overview.summary.expansionary}</div>
                      <div className="mt-2 text-sm" style={{ color: ui.textSecondary }}>Indicators currently leaning supportive.</div>
                    </CardContent>
                  </Card>
                  <Card style={cardStyle}>
                    <CardContent className="p-4">
                      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Caution Breadth</div>
                      <div className="text-2xl font-semibold" style={{ color: bucketTone.cautionary.text }}>{overview.summary.cautionary}</div>
                      <div className="mt-2 text-sm" style={{ color: ui.textSecondary }}>Indicators signaling mixed or yellow-light conditions.</div>
                    </CardContent>
                  </Card>
                  <Card style={cardStyle}>
                    <CardContent className="p-4">
                      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Recession Breadth</div>
                      <div className="text-2xl font-semibold" style={{ color: bucketTone.recessionary.text }}>{overview.summary.recessionary}</div>
                      <div className="mt-2 text-sm" style={{ color: ui.textSecondary }}>Indicators that currently read defensive.</div>
                    </CardContent>
                  </Card>
                  <Card style={cardStyle}>
                    <CardContent className="p-4">
                      <div className="text-xs uppercase tracking-wide mb-2" style={{ color: ui.textMuted }}>Data Coverage</div>
                      <div className="text-2xl font-semibold">
                        {freshnessSummary ? `${freshnessSummary.totalSeries - freshnessSummary.staleCount}/${freshnessSummary.totalSeries + (overview.missing_indicators?.length ?? 0)}` : "—"}
                      </div>
                      <div className="mt-2 text-sm" style={{ color: ui.textSecondary }}>
                        {freshnessSummary
                          ? `Daily ${freshnessSummary.cadenceCounts.daily} • Monthly ${freshnessSummary.cadenceCounts.monthly} • Quarterly ${freshnessSummary.cadenceCounts.quarterly}${(overview.missing_indicators?.length ?? 0) > 0 ? ` • ${overview.missing_indicators!.length} feed error(s)` : ""}`
                          : "Coverage unavailable"}
                      </div>
                    </CardContent>
                  </Card>
                </div>
                </section>

                <section id="econ-sec-drivers" className="scroll-mt-20">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <Card style={cardStyle}>
                    <CardContent className="p-5">
                      <h2 className="text-lg font-semibold mb-3">Top Supports</h2>
                      <div className="space-y-3">
                        {overview.drivers.supports.length ? overview.drivers.supports.map((item) => (
                          <div key={item.series_id} className="rounded-lg p-3" style={softCardStyle}>
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="font-medium">{item.label}</div>
                                <div className="text-xs" style={{ color: ui.textMuted }}>{item.type} • {item.class}</div>
                              </div>
                              <span
                                className="text-xs px-2 py-1 rounded-full"
                                style={{
                                  backgroundColor: bucketTone.expansionary.chipBg,
                                  color: bucketTone.expansionary.text,
                                  border: `1px solid ${bucketTone.expansionary.border}`,
                                }}
                              >
                                {item.score_label}
                              </span>
                            </div>
                          </div>
                        )) : <div style={{ color: ui.textMuted }}>No support signals available.</div>}
                      </div>
                    </CardContent>
                  </Card>

                  <Card style={cardStyle}>
                    <CardContent className="p-5">
                      <h2 className="text-lg font-semibold mb-3">Primary Watch Items</h2>
                      <div className="space-y-3">
                        {overview.drivers.risks.length ? overview.drivers.risks.map((item) => {
                          const tone = item.score >= 2 ? bucketTone.expansionary : item.score === 1 ? bucketTone.cautionary : bucketTone.recessionary;
                          return (
                            <div key={item.series_id} className="rounded-lg p-3" style={softCardStyle}>
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="font-medium">{item.label}</div>
                                  <div className="text-xs" style={{ color: ui.textMuted }}>{item.type} • {item.class}</div>
                                </div>
                                <span
                                  className="text-xs px-2 py-1 rounded-full"
                                  style={{
                                    backgroundColor: tone.chipBg,
                                    color: tone.text,
                                    border: `1px solid ${tone.border}`,
                                  }}
                                >
                                  {item.score_label}
                                </span>
                              </div>
                            </div>
                          );
                        }) : <div style={{ color: ui.textMuted }}>No risk signals available.</div>}
                      </div>
                    </CardContent>
                  </Card>
                </div>
                </section>

                <section id="econ-sec-indicators" className="scroll-mt-20">
                <Card style={cardStyle}>
                  <CardContent className="p-5">
                    <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
                      <div>
                        <h2 className="text-lg font-semibold">Indicator Monitor</h2>
                        <p className="text-sm" style={{ color: ui.textSecondary }}>
                          Full indicator inventory grouped by macro decision bucket. Click any indicator to open the controllable time-series view.
                        </p>
                      </div>
                      <div className="text-xs" style={{ color: ui.textMuted }}>
                        Snapshot generated {fmtLocalDate(overview.generated_at)}
                      </div>
                    </div>

                    <div className="space-y-5">
                      {groupedIndicators.map(([groupName, items]) => (
                        <div key={groupName} id={groupAnchorId(groupName)} className="scroll-mt-20">
                          <h3 className="text-base font-semibold mb-3">{groupName}</h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {items.map((item) => {
                              const tone = item.score >= 2 ? bucketTone.expansionary : item.score === 1 ? bucketTone.cautionary : bucketTone.recessionary;
                              const history = overview.histories[item.series_id];
                              const cadenceFromItem = (item.cadence || "").toLowerCase();
                              const cadence: CadenceBucket = cadenceFromItem.includes("day")
                                ? "daily"
                                : cadenceFromItem.includes("week")
                                  ? "weekly"
                                  : cadenceFromItem.includes("month")
                                    ? "monthly"
                                    : cadenceFromItem.includes("quarter")
                                      ? "quarterly"
                                      : inferCadenceFromHistory(history);
                              return (
                                <button
                                  key={item.series_id}
                                  type="button"
                                  className="rounded-lg p-4 text-left transition hover:-translate-y-[1px] hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                                  style={{ ...softCardStyle, cursor: "pointer" }}
                                  onClick={() => openIndicatorHistory(item)}
                                  aria-label={`Open history for ${item.label}`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <div className="font-medium">{item.label}</div>
                                      <div className="text-xs mt-1" style={{ color: ui.textMuted }}>
                                        {item.class} • {titleCase(cadence)}
                                      </div>
                                    </div>
                                    <span
                                      className="text-xs px-2 py-1 rounded-full whitespace-nowrap"
                                      style={{
                                        backgroundColor: tone.chipBg,
                                        color: tone.text,
                                        border: `1px solid ${tone.border}`,
                                      }}
                                    >
                                      {item.score_label}
                                    </span>
                                  </div>
                                  <div className="mt-3 text-sm" style={{ color: ui.textSecondary }}>
                                    <div>Latest value: <span className="font-medium" style={{ color: ui.textPrimary }}>{formatIndicatorValue(item)}</span></div>
                                    <div>Updated: {fmtLocalDate(item.date)}</div>
                                    {item.include_in_breadth === false ? (
                                      <div className="mt-1 text-xs" style={{ color: ui.textMuted }}>Context only • excluded from AML and breadth</div>
                                    ) : null}
                                  </div>
                                  <div className="mt-3 text-xs font-medium" style={{ color: chart.link }}>
                                    Open full history →
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                </section>
              </div>
            ) : null}
          </TabsContent>

          <TabsContent value="ml" className="mt-4">
            <MLResultsTab />
          </TabsContent>
        </Tabs>
        <IndicatorHistoryModal
          indicator={selectedIndicator}
          history={selectedIndicatorHistory}
          isLoading={historyLoading}
          loadError={historyError}
          selectedTimeframe={selectedTimeframe}
          onTimeframeChange={setSelectedTimeframe}
          onClose={closeIndicatorHistory}
        />
        <MethodologyModal
          open={methodologyOpen}
          onClose={() => setMethodologyOpen(false)}
          sentinelThreshold={overview?.early_warning?.thresholds?.sentinel ?? null}
          watchThreshold={overview?.early_warning?.thresholds?.watch ?? null}
          cautionThreshold={overview?.early_warning?.thresholds?.caution ?? null}
          severeThreshold={overview?.early_warning?.thresholds?.severe ?? null}
        />
      </div>
    </Layout>
  );
};

export default EconomicDashboard;